// ============================================================
// TERROR TCG — RULES ENFORCER
// ============================================================
// SINGLE SOURCE OF TRUTH for all game rule validation.
// Every rule from the rulebook is enforced here.
// Every game action is validated against these rules.
// No exceptions. No loopholes.
//
// Consumed by:
//   - Shatterfield.js (every action dispatch, via ruleAdapter)
//   - DeckBuilder.js (deck validation)
//   - Debugger.js (rule checks)
//
// All Shatterfield state is normalized through ruleAdapter.js before it
// reaches these validators, so field-name and effective-stat differences
// can never cause the gate to disagree with the board.
//
// Structure:
//   - Validators: pure functions that check game state
//   - Enforcers: higher-level rule categories
//   - Results: { valid: bool, violations: [code, ...], reason: string }
// ============================================================

import {
  STARTING_ANIMA, MAX_ANIMA, STARTING_KHAO, MAX_KHAO_NAT, MAX_KHAO_ABS,
  STARTING_HAND, HAND_LIMIT, ESCLING_SLOTS, TOME_SLOTS, CURIO_SLOTS,
  GRAVESHIFT_COST, GRIMOIRE_MIN, LUXMOIRE_MIN, LUXMOIRE_MAX,
  DECK_LIMITS, RESURRECT_COST, HERETICAL_TRAINING_COST, TOME_WAIT,
  PERSISTENT_TOME_TAGS, RULES,
  // R101 — the ONE Deviant fare table (see rules.js DEVIANT_FARE).
  deviantFare,
  // ── THE RESURRECTION & PRIMERA WAVE (July 25, 2026) ──
  // RESURRECT_LIMIT — per-instance tallies: Beb 3× / Champion 2× / Lord 1×.
  // PRIMERAS / PRIMERA_COST — the eight stamps + level-priced activation.
  // rules.js is the ONE source of truth (H22 mandate); nothing retyped here.
  RESURRECT_LIMIT, PRIMERAS, PRIMERA_COST,
  // ── THE COHERENCY PASS (2026-08-25) ──
  // canPayVoluntary is R96's ONE implementation — the till law is a
  // strict `>`, and every hand-rolled `>=` at a paywall is the bug it
  // was written to kill. FIRST_KHAO_TURN/MAX_KHAO_NAT price R91/R13.
  canPayVoluntary, FIRST_KHAO_TURN, khaoClimbFloor,
  // ── THE 2026-08-25 RULINGS ──
  // openingCycleLength / isLytAttack come from rules.js so the window
  // R08 covers and the word R08 uses are declared exactly once. See
  // the ATTACK vs LYT ATTACK and THE OPENING CYCLE banners there.
  openingCycleLength, isLytAttack, MULLIGAN_LIMIT,
  // ── THE 2026-08-26 RULING ──
  // isEffectSearch tells a PLAYER'S search action from a CARD EFFECT
  // that searches. R08 bars the first on the opening Cycle and exempts
  // the second. The predicate lives in rules.js beside isLytAttack for
  // the same reason: a distinction the whole engine must agree on is
  // declared exactly once.
  isEffectSearch,
  // ── THE 2026-08-26 CANON PASS ──
  // R104 the U'hlti deck cap · R62 the Ally-Ilk Curio cap ·
  // R106 what a Luxmoire search may find · R107 the no-mixing law ·
  // R108 the phase gate. Every one of them is DECLARED in rules.js and
  // merely ASKED here — this file has no private copy of any of them.
  LUXMOIRE_UHLTI_MAX, ALLY_ILK_CURIO_MAX, isLuxSearchable,
  farePoolsFor, canPayFare, phaseAllows, phaseOf, PHASE,
  // ── THE EXILE WAVE (caP'n, 2026-09-06/07) ──────────────────
  // MIND_RIFT              R119 — the Warden's rift, which never walls Anima.
  // wardenOpenToLytAttack  R119 — may that rift's being be swung at yet.
  // mindMayAct             R121 — the Mind's Silence, as one predicate.
  // exileBarsResurrection  R113 — a card that spent its one return is gone.
  // rolesAwake             R115 — nothing role-shaped works for 3 full Turns.
  MIND_RIFT, BODY_RIFT, wardenOpenToLytAttack, animaOpenToLytAttack,
  blocksAnimaLytAttack, riftHeld,
  mindMayAct, mindIsSilenced, exileBarsResurrection, rolesAwake,
  riftArtIsAwake, roleFare, roleCooldown, exileFare, exiliationCooldown,
  exileRetrievalFare, exileRetrievalCooldown, canReachExileRift,
  EXILE_RETRIEVAL_ANIMA_FLOOR, WARDEN_ARTS_PER_TURN, isAenarchistArt,
  EXILIATION_OPTION_IDS, isExileSpent, roleOfRift, riftNameOf,
  ROLE_LOCK_FULL_TURNS, ROLES,   // canPayVoluntary is already imported above
  soulCardOffCooldown, tomeWaitHalfTurns, lytAttacksAllowed,
  // ── R122 · THE CADENCE, FOR A TOME AS WELL AS A SOUL CARD ───
  // cooldownHalfTurns is rules.js's ONE definition of "once per N full
  // Turns, counted in half-turns" and it carries the seat count, so a
  // 3-4 seat table measures a full Turn as a full lap for free.
  // halfTurnsPerFullTurn is the same arithmetic's numerator, needed by
  // R119's per-AENARCHIST-per-TURN cap, which is a FULL-Turn window.
  cooldownHalfTurns, halfTurnsPerFullTurn,
} from "./rules";
import { allCardsFlat } from "./ruleAdapter";

// ═══════════════════════════════════════════════════════════
// VALIDATION RESULT BUILDER
// ═══════════════════════════════════════════════════════════
const pass = () => ({ valid: true, violations: [], reason: null });

const fail = (violations = [], reason = "") => ({
  valid: false,
  violations: Array.isArray(violations) ? violations : [violations],
  reason,
});

const multi = (...results) => {
  const all = results.filter(r => r && !r.valid);
  if (all.length === 0) return pass();
  return fail(
    all.flatMap(r => r.violations),
    all.map(r => r.reason).filter(Boolean).join("; ")
  );
};

// ═══════════════════════════════════════════════════════════
// CORE UTILITIES
// ═══════════════════════════════════════════════════════════

/**
 * Extract card from a flat card database.
 * Used throughout to avoid null errors.
 */
export const getCard = (id, allCards = null) => {
  if (!id) return null;
  const db = Array.isArray(allCards) && allCards.length ? allCards : allCardsFlat();
  return db.find(c => c && c.id === id) || null;
};

/**
 * Count instances of a card by name in an array.
 */
const countByName = (cardArray, name) => {
  return (cardArray || []).filter(id => {
    // If it's an object, get the id prop; if it's a string, use it directly
    const cardId = typeof id === "string" ? id : id?.id;
    return cardId === name;
  }).length;
};

/**
 * Is this being a Natural being (no Deviant mark)?
 *
 * ⚠ ANY POSITIVE DEVIANT SIGNAL WINS, AND THE ORDER USED TO DECIDE IT.
 * This read `isDeviant` and RETURNED, so a slot carrying
 * `{ isDeviant: false, natural: false }` — which is what a normalised
 * copy of a board slot that only ever wrote `natural` looks like — was
 * called Natural here while Shatterfield's own U'hlti guard
 * (`!slot.natural`) called it Deviant. Three fields, first-one-wins, and
 * the winner was whichever the adapter happened to emit.
 *
 * Now: Deviant if ANY of the three says so; Natural only if none does.
 * Absent-everything still reads as Natural, so no stored duel moves.
 */
const isNaturalBeing = (slot) => {
  if (!slot) return false;
  if (slot.isDeviant === true) return false;
  if (slot.deviant === true) return false;
  if (slot.natural === false) return false;
  return true;
};

/**
 * What level is this being? (L1 = 1, L2 = 2, L3 = 3, escling = 0)
 */
const getBeingLevel = (slot) => {
  if (!slot) return 0;
  if (slot.escling) return 0; // esclings are level 0
  // Normalized slots carry an explicit `level`; trust it.
  if (typeof slot.level === "number") return slot.level;
  // Raw fallback: resolve from the escendant card id.
  if (slot.escendant) {
    const c = getCard(slot.escendant);
    return c?.level || 0;
  }
  return 0;
};

/**
 * Is a being Fallen (can only be hit by Anima, not Lyt)?
 */
const isFallenBeing = (slot) => {
  return !!(slot && (slot.isFallen || slot.fallen));
};

/**
 * Is a being Fractured (Lyf at 0)?
 */
const isFractured = (slot) => {
  if (!slot) return false;
  const lyf = slot.lyf !== undefined ? slot.lyf
            : (slot.currentLyf !== null && slot.currentLyf !== undefined ? slot.currentLyf : null);
  return lyf === 0;
};

/**
 * Count beings of a given SCHOOL (Space / Phantom / Dragon / Time) on a
 * field. Named for the Ilk axis until 2026-08-25; the words it is
 * asked for were always Family B, and `card.school` is now where they
 * live. Currently unreferenced — kept because the counting Tomes it
 * mirrors are enforced elsewhere, and a helper that reads the wrong
 * field is worse than one that reads none.
 */
const countBeingsOfSchool = (field, cls) => {
  return (field?.esclings || []).filter(slot => {
    const card = getCard(slot?.escendant);
    return card && card.school === cls;
  }).length;
};

/**
 * Is a slot empty (no escling, no escendant)?
 */
const isEmpty = (slot) => !slot || (!slot.escling && !slot.escendant);

/**
 * How many Impera are attached to a being?
 */
const countSoul = (slot) => {
  return (slot?.soul || []).length;
};

/**
 * Does a being have an attached U'hlti? (real prefix: u_)
 */
const hasUhlti = (slot) => {
  return (slot?.soul || []).some(entry => {
    const id = typeof entry === "string" ? entry : entry?.id;
    return typeof id === "string" && id.startsWith("u_");
  });
};

/**
 * Does a being have an attached Affinity? (real prefix: aff_ — NOT a_)
 */
const hasAffinity = (slot) => {
  return (slot?.soul || []).some(entry => {
    const id = typeof entry === "string" ? entry : entry?.id;
    return typeof id === "string" && id.startsWith("aff_");
  });
};

/**
 * Does a being have a Heretically-bonded Tome?
 *
 * ⭐ TWO SHAPES, AND THE FIRST ONE IS THE REAL ONE (2026-08-20).
 * This used to check only for an `ht_*` id sitting IN THE SOUL — a
 * scheme that (a) required minting a parallel "heretical" edition of
 * every Tome, none of which exists in cards.js, and (b) contradicted
 * R68 outright, because anything in the soul comes back on a Resurrect
 * and a Heretical Tome must not.
 *
 * The engine built for it instead binds ANY Tome to `slot.heretical`,
 * a field of its own, which makes R68 true by construction. The `ht_`
 * fallback is kept because it costs one line and a save written under
 * the old assumption should not silently lose its attachment.
 */
const hasHereticalTome = (slot) => {
  if (slot?.heretical) return true;
  return (slot?.soul || []).some(entry => {
    const id = typeof entry === "string" ? entry : entry?.id;
    return typeof id === "string" && id.startsWith("ht_");
  });
};

/**
 * Count how many times a specific being instance has been resurrected this solrend.
 */
const countResurrectionsThisSolrend = (slotHistory, slot) => {
  // If we're tracking resurrection history, count it here
  // For now, this is a placeholder — the actual implementation
  // will depend on your match state structure.
  return slot?.resurrectionsUsed || 0;
};

// ═══════════════════════════════════════════════════════════
// SETUP & DECK VALIDATION
// ═══════════════════════════════════════════════════════════

/**
 * R01 — Both players start with 30 Anima (max 50)
 */
export const validateStartingAnima = (anima) => {
  if (anima !== STARTING_ANIMA) {
    return fail("R01", `Starting Anima must be ${STARTING_ANIMA}, not ${anima}`);
  }
  return pass();
};

/**
 * R02 — Both players start with 3 Max Khao
 */
export const validateStartingKhao = (maxKhao) => {
  if (maxKhao !== STARTING_KHAO) {
    return fail("R02", `Starting Max Khao must be ${STARTING_KHAO}, not ${maxKhao}`);
  }
  return pass();
};

/**
 * R03 — Starting hand = 5 cards each
 */
export const validateStartingHand = (handSize) => {
  if (handSize !== STARTING_HAND) {
    return fail("R03", `Starting hand must be ${STARTING_HAND}, not ${handSize}`);
  }
  return pass();
};

/**
 * R04 — 4 Esclings placed before duel begins, one per battle rift (R110)
 */
export const validateEsclingPlacement = (esclings) => {
  const count = (esclings || []).filter(s => s && s.escling).length;
  if (count !== ESCLING_SLOTS) {
    return fail("R04", `Must have exactly ${ESCLING_SLOTS} Esclings, found ${count}`);
  }
  return pass();
};

/**
 * R05 — Grimoire min 35 cards (Escendants + Tomes only — lane
 * composition is checked by validateDeckLanes below)
 */
export const validateGrimoireSize = (grimoire) => {
  const size = (grimoire || []).length;
  if (size < GRIMOIRE_MIN) {
    return fail("R05", `Grimoire must have at least ${GRIMOIRE_MIN} cards, found ${size}`);
  }
  return pass();
};

/**
 * R06 — Luxmoire min 10, max 40 (Impera + U'hltis + Affinities)
 */
export const validateLuxmoireSize = (luxmoire) => {
  const size = (luxmoire || []).length;
  if (size < LUXMOIRE_MIN) {
    return fail("R06", `Luxmoire must have at least ${LUXMOIRE_MIN} cards, found ${size}`);
  }
  if (size > LUXMOIRE_MAX) {
    return fail("R06", `Luxmoire must have at most ${LUXMOIRE_MAX} cards, found ${size}`);
  }
  return pass();
};

/**
 * R05/R06 — THE DECK LANES (caP'n, 2026-08-26)
 *
 *   *"the way the game currently works is correct. Grimoire —
 *   Escendants and Tomes only. Luxmoire — everything else that doesn't
 *   immediately get set to the board, like Curio and Prophecy."*
 *
 * Grimoire = Escendants + Tomes ONLY.
 * Luxmoire = Impera + U'hltis + Affinities
 *            (max 1 Affinity R60; max 3 U'hltis R104).
 * Esclings, Prophecies, and Curios are PRE-PLACED — never deck contents.
 * Accepts {cardId: qty} objects (the persisted deck shape).
 *
 * ⚠ THE CITATION THIS REPLACES WAS FALSE. It read "Rulez v2, July
 * 2026", and Rulez v2 — the APRIL book — says the OPPOSITE on every
 * clause: ch.3 puts U'hltis, Curios, Prophecies and Affinities in the
 * Grimoire and gives the Luxmoire "only Primera", 10-30. NO BEHAVIOUR
 * CHANGED WITH THIS EDIT; the lanes below are byte-for-byte the ones
 * that shipped. Only the authority for them did.
 */
export const validateDeckLanes = (grimoireObj, luxmoireObj, allCards = []) => {
  const violations = [];
  const illegal = [];
  for (const id of Object.keys(grimoireObj || {})) {
    const t = getCard(id, allCards)?.type;
    if (t && t !== "Escendant" && t !== "Tome") { violations.push("R05"); illegal.push(`${id} (${t}) in Grimoire`); }
  }
  let affinities = 0;
  for (const [id, qty] of Object.entries(luxmoireObj || {})) {
    const t = getCard(id, allCards)?.type;
    if (t && t !== "Impera" && t !== "U'hlti" && t !== "Affinity") { violations.push("R06"); illegal.push(`${id} (${t}) in Luxmoire`); }
    if (t === "Affinity") affinities += qty || 0;
  }
  if (affinities > 1) { violations.push("R60"); illegal.push(`${affinities} Affinities in Luxmoire (max 1)`); }
  // ── R104 · THREE U'HLTIS TO A LUXMOIRE (caP'n, 2026-08-26) ──
  // A SECOND SHAPE OF LIMIT ON THE SAME CARD TYPE, which is why it
  // cannot live in DECK_LIMITS beside R59: DECK_LIMITS is keyed
  // per-unique-name and validateDeckLimits counts it that way, so
  // UHLTI:1 there means "one of each name" and says nothing at all
  // about the total. This is the total, and it is checked in the LANE
  // validator because a lane is exactly the scope the rule names — "3
  // U'hltis if I'm not mistaken", per Luxmoire.
  let uhltis = 0;
  for (const [id, qty] of Object.entries(luxmoireObj || {})) {
    if (getCard(id, allCards)?.type === "U'hlti") uhltis += qty || 0;
  }
  if (uhltis > LUXMOIRE_UHLTI_MAX) {
    violations.push("R104");
    illegal.push(`${uhltis} U'hltis in Luxmoire (max ${LUXMOIRE_UHLTI_MAX})`);
  }
  if (violations.length > 0) return fail(violations, `Lane violations: ${illegal.join("; ")}`);
  return pass();
};

/**
 * R62 — ALLY-ILK CURIOS: 2 PER SHATTERFIELD (caP'n, 2026-08-26).
 *
 * ⚠ THIS VALIDATOR EXISTS BECAUSE THE RULE WAS DECLARED UNCHECKABLE AND
 * WAS NOT. GameStateValidator recorded R62 as "'Curio Ally Ilk' has no
 * field in cards.js to read" — which was simply false: curio_beb_buddy
 * carries `ilk: "ally"`, and Debugger.js has counted it correctly the
 * whole time. One part of the engine read the field while another said
 * the field did not exist.
 *
 * The cap is 2 and it matches CURIO_SLOTS: both of your Curio rifts may
 * hold an Ally. Note that R61 (1 per unique name) still binds on top,
 * so with today's single Ally-Ilk Curio the practical maximum is 1 —
 * the law is written for the card set, not for this week's card set.
 *
 * `curios` is the PRE-PLACED list (ids), not a deck.
 */
export const validateAllyIlkCurios = (curios, allCards = []) => {
  const ally = (curios || []).filter((id) => getCard(id, allCards)?.ilk === "ally").length;
  if (ally > ALLY_ILK_CURIO_MAX) {
    return fail("R62", `${ally} Ally-Ilk Curios pre-placed (max ${ALLY_ILK_CURIO_MAX} per Shatterfield)`);
  }
  return pass();
};

/**
 * R53–R63 — Deck limits by rarity and name
 */
// ── THE RULE CODE LIVES ON THE DATA, NOT ON THE KEY ORDER ────
// This used to be `"R" + (53 + Object.keys(DECK_LIMITS).indexOf(type))`,
// which was correct only by accident of the order the keys happened to
// be typed in — and DECK_LIMITS gained IMPERA_L1/L2/L3 (R94) on
// 2026-08-25, at which point every code from UHLTI onward silently
// shifted by three. An explicit map cannot rot that way, and a key
// added to rules.js with no entry here fails loudly below instead of
// reporting somebody else's rule.
export const DECK_LIMIT_RULE = {
  ESCENDANT_L1: "R53",
  ESCENDANT_L2: "R54",
  ESCENDANT_L3: "R55",
  TOME_L1:      "R56",
  TOME_L2:      "R57",
  TOME_L3:      "R58",
  UHLTI:        "R59",
  AFFINITY:     "R60",
  CURIO:        "R61",
  PROPHECY:     "R63",
  IMPERA_L1:    "R94",
  IMPERA_L2:    "R94",
  IMPERA_L3:    "R94",
};

/**
 * The DECK_LIMITS bucket a card falls in.
 * ⚠ cards.js used to call the Luxmoire attachment type "Primera" while
 * the catalogue (R69, R94, DECK_LIMITS.IMPERA_L*) called it IMPERA.
 * Until this line existed, every Impera in the game had NO deck limit at
 * all because getCardType returned null for its type. cards.js now types
 * them "Impera" — the "Primera" arm below is kept ONE RELEASE for any
 * card object rehydrated from an older payload, and can be dropped next.
 */
export const deckLimitKey = (card) => {
  if (!card) return null;
  if (card.type === "Escendant") return `ESCENDANT_L${card.level}`;
  if (card.type === "Tome") return `TOME_L${card.level}`;
  if (card.type === "Primera" || card.type === "Impera") return `IMPERA_L${card.level}`;
  if (card.type === "U'hlti") return "UHLTI";
  if (card.type === "Affinity") return "AFFINITY";
  if (card.type === "Curio") return "CURIO";
  if (card.type === "Prophecy") return "PROPHECY";
  return null;
};

export const validateDeckLimits = (deck, allCards = []) => {
  const getCardType = (cardId) => deckLimitKey(getCard(cardId, allCards));

  // ── ONE LANE, EITHER SHAPE ──────────────────────────────────────
  // ⚠ THIS TOOK AN ARRAY WHILE validateDeckLanes — its partner, called
  // on the same lane, in the same breath — TOOK `{cardId: qty}`, which
  // is the shape a deck is actually PERSISTED in. So the two halves of
  // deck legality could not be asked with one value, and the one that
  // took the persisted shape was the one that got wired. That is why
  // DeckBuilder grew its own hand-rolled per-name counting instead of
  // calling this, and it is half the reason this function had never
  // executed. It reads both shapes now; a `{id: qty}` map is expanded to
  // the multiset it denotes, because a per-name CAP is a question about
  // copies and a Set cannot see the fifth copy of an L3 (trap §4.3).
  if (deck && !Array.isArray(deck) && typeof deck === "object") {
    const flat = [];
    for (const [id, qty] of Object.entries(deck)) {
      const n = Math.max(0, Math.floor(Number(qty) || 0));
      for (let i = 0; i < n; i++) flat.push(id);
    }
    deck = flat;
  }
  if (!Array.isArray(deck)) deck = [];

  const violations = [];
  const typeCount = {}; // { "Escendant:unique_name": count, ... }
  // R60 is PER LUXMOIRE, not per name and not deck-wide-in-general.
  // `deck` here IS one lane, so a lane-wide count is the per-Luxmoire
  // count — and an Affinity found in a GRIMOIRE is an R05 lane
  // violation (validateDeckLanes owns that), never an R60 one.
  let affinityCount = 0;
  let prophecyCount = 0; // R63 — only 1 Prophecy per Solrend
  // (A July 2026 fix, mis-cited as Rulez v2's: the counters here were
  // dead consts, so multiple Affinities/Prophecies slipped through.)

  for (const cardId of deck) {
    const cardType = getCardType(cardId);
    if (!cardType) continue;

    const limit = DECK_LIMITS[cardType];
    if (!limit) continue; // No limit for this type

    // For Affinity and Prophecy, use deck-wide limits
    if (cardType === "AFFINITY") {
      affinityCount += 1;
      if (affinityCount > 1) {
        violations.push("R60");
      }
    } else if (cardType === "PROPHECY") {
      prophecyCount += 1;
      if (prophecyCount > 1) {
        violations.push("R63");
      }
    } else {
      // For all others, use unique-name limits
      const key = `${cardType}:${cardId}`;
      typeCount[key] = (typeCount[key] || 0) + 1;
      if (typeCount[key] > limit) {
        violations.push(DECK_LIMIT_RULE[cardType] || "R05");
      }
    }
  }

  if (violations.length > 0) {
    return fail(violations, `Deck violates copy limits`);
  }
  return pass();
};

// ═══════════════════════════════════════════════════════════
// TURN STRUCTURE & RESOURCE MANAGEMENT
// ═══════════════════════════════════════════════════════════

// ── THE TURN BASE, NORMALIZED DEFENSIVELY ────────────────────
// Shatterfield and gameManager count `match.turn` in ZERO-BASED
// HALF-turns (createMatch writes turn: 0). This enforcer was written
// against a ONE-BASED turn number and tested `turnNumber !== 1`, so
// every R07/R08 check silently did nothing on the real opening Turn
// and fired on the wrong one instead.
//
// ⚠ REPORTED, NOT OWNED HERE. ruleAdapter.normalizeGameState is the
// one place the two dialects are allowed to meet and it should own
// this normalization (see the audit note). Until it does, this
// function accepts EITHER base and treats both 0 and 1 as "the
// opening Turn", which is true in both dialects and cannot be wrong
// in either.
// ⚠ SEAT-COUNT AWARE SINCE THE R08 REWRITE (2026-08-25). The window is
// THE OPENING CYCLE — each seat's first Turn — not a fixed two
// half-turns. In 1v1 `openingCycleLength(2)` is 2 and this is the
// byte-identical `t <= 1` it replaced, so classic duels are untouched;
// a 3-4p FFA or a 2v2 now gets the same law over its own first cycle
// instead of two seats getting it and the other two not.
export const isOpeningTurn = (turnNumber, seatCount = 2) => {
  const t = Number(turnNumber);
  if (!Number.isFinite(t)) return false;
  return t < openingCycleLength(seatCount);
};

// ═══════════════════════════════════════════════════════════
// 🌒 R08 · THE OPENING CYCLE (caP'n ruling, 2026-08-25)
// ═══════════════════════════════════════════════════════════
// caP'n, verbatim:
//
//   *"Turn 1, neither player can Attack in any form, remember 'Attack'
//   in Terror is a very broad term that can mean you used a tome or
//   Impera or anything to 'attack' a being or your adversary directly,
//   Lyt Attack is the actual move every being gets 1 of per turn. So
//   neither can Attack at all first turn, nor can they draw from their
//   Grimoire. Both players can draw 1 card that turn from their
//   Luxmoire, *new* they are also not allowed to search their Luxmoire
//   that first turn, nor Grimoire. Player to go second can draw from
//   their Grimoire but not search it, and as always, if you draw from
//   one Book(deck) you cannot draw from the other that turn (card
//   effects excluded). Either player, if they have not drawn yet, they
//   can do a Mulligan on that first turn and no other."*
//
// WHAT CHANGED HERE, item by item, and what was already true:
//
//   · ATTACK IS NOW THE BROAD TERM. This function used to refuse only
//     `attackType === "Lyt"`, so an Anima strike, a Tome, an Impera, a
//     Curio or a Primera stamp on the opening Cycle was LEGAL. It now
//     refuses every kind, and `isLytAttack` (rules.js) is the only
//     place the two words are told apart. The refusal MESSAGE still
//     names which kind was attempted, because "no Lyt Attacks" and
//     "no Attacks of any kind" are different findings.
//   · SEARCHING EITHER BOOK IS NEW AND IS REFUSED. R88 owns the fares;
//     R08 suspends them entirely on the opening Cycle, so the fare is
//     never even reached. This is the clause caP'n marked "*new*".
//     ⚠ NARROWED 2026-08-26 — see the next note.
//
// ── THE CARD-EFFECT EXEMPTION (caP'n, 2026-08-26) ────────────
// caP'n, verbatim: *"brilliant distinction yes, cards are exempt from
// the first turn search rule"*.
//
// The search bar covers the PLAYER'S OWN SEARCH ACTION and nothing
// else: the deliberate move that pays an R88 fare and digs through a
// Book. A CARD that searches is exempt and resolves normally on the
// opening Cycle. `effectSearch` is the discriminator and it is the
// exact twin of the draw exemption caP'n wrote into the same ruling —
// "(card effects excluded)", modelled as `effectDrewThisTurn`. One
// idea, two clauses, named alike.
//
// ⚠ IT MUST BE CLAIMED. Absent `effectSearch`, a search is the
// player's and stays barred — which is what every call site that
// predates this ruling looks like, so the ruling can only ever loosen
// a door somebody deliberately opened.
//
// ⚠ AND IT IS THE SEARCH BAN ONLY. THE SEARCH BAN AND THE ATTACK BAN
// ARE DIFFERENT BANS AND A CARD CAN BE CAUGHT BY ONE AND NOT THE
// OTHER. The Machine and Humanoid Primera stamps search a Book and are
// exempt here; the Phantom and Beast stamps damage the adversary and
// are still refused below by the ATTACK clause, which this exemption
// does not touch. `validateAction`'s PRIMERA case asks the Attack
// question separately, and either ban alone is enough to stop a press.
//   · R07 IS UNCHANGED AND WAS ALREADY RIGHT: only the seat going
//     FIRST loses its Grimoire draw, and the Luxmoire draw is legal for
//     both seats. The seat going second may draw from its Grimoire.
//   · R11 IS UNCHANGED AND WAS ALREADY ENFORCED: one draw per Turn,
//     one lane. caP'n calls it "as always" and the code agrees — this
//     leg predates the ruling.
//
// `action` is a STRING: "draw" | "attack" | "search" | "mulligan".
export const validateFirstTurnRestrictions = (
  turnNumber,
  isFirstPlayer,
  action,
  { source = "GRIMOIRE", attackType = "Lyt", drawsThisTurn = 0, seatCount = 2,
    effectSearch = false } = {}
) => {
  // R11 — one draw per Turn, in EVERY Turn including the first, and
  // drawing from one Book bars the other. Card effects are excluded:
  // an effect draw never calls this with action "draw".
  if (action === "draw" && (drawsThisTurn || 0) > 0) {
    return fail("R11", "One draw per Turn — Grimoire OR Luxmoire, never both");
  }

  if (!isOpeningTurn(turnNumber, seatCount)) return pass(); // no opening-Cycle restrictions after that

  if (action === "draw" && isFirstPlayer && String(source).toUpperCase() !== "LUXMOIRE") {
    return fail("R07", "The seat going first has no Grimoire draw on its opening Turn (the Luxmoire draw is allowed)");
  }

  // ── R08 · NO ATTACKS OF ANY KIND ─────────────────────────────
  if (action === "attack") {
    return isLytAttack(attackType)
      ? fail("R08", "No Lyt Attacks on the opening Cycle")
      : fail("R08", `No Attacks of any kind on the opening Cycle — a ${attackType} Attack is still an Attack`);
  }

  // ── R08 · NEITHER BOOK MAY BE SEARCHED ───────────────────────
  // R88 is named alongside R08 because R88 is the rule the player
  // thinks they are using when they reach for a search; saying only
  // "R08" in the toast would send them to the wrong page.
  if (action === "search") {
    // THE EXEMPTION, asked FIRST — a card effect never reaches the bar,
    // and it never reaches R88 either, because the card paid its own
    // cost and R88 prices the player's search action. (caP'n,
    // 2026-08-26: *"cards are exempt from the first turn search rule"*.)
    if (isEffectSearch(effectSearch)) return pass();
    const book = String(source).toUpperCase() === "LUXMOIRE" ? "Luxmoire" : "Grimoire";
    return fail(["R08", "R88"], `Neither Book may be searched on the opening Cycle — the ${book} search fare is suspended, not merely unpayable (a CARD EFFECT that searches is exempt; this is the player's own search)`);
  }

  // R09 — Transcend / Ascend / activation are explicitly LEGAL on
  // Turn 1 for the first player. Named so nobody re-adds a blanket ban.
  return pass();
};

/**
 * R106 — a LUXMOIRE search may only find an Impera (caP'n, 2026-08-26).
 * `found` is the card or card id the search is about to hand over;
 * omit it to validate the ACT of searching rather than its result.
 * Grimoire searches are not bound by this and pass straight through.
 */
export const validateLuxSearchTarget = (source, found = null, allCards = []) => {
  if (String(source || "GRIMOIRE").toUpperCase() !== "LUXMOIRE") return pass();
  if (found == null) return pass();
  const card = typeof found === "string" ? getCard(found, allCards) : found;
  if (!card) return pass();
  if (!isLuxSearchable(card)) {
    return fail("R106", `A Luxmoire search finds Impera only — ${card.name || card.id} is ${card.type ? "a " + card.type : "not an Impera"}. U'hltis and Affinities must be DRAWN`);
  }
  return pass();
};

/**
 * R90 — THE MULLIGAN, TIGHTENED (caP'n ruling, 2026-08-25).
 *
 * *"Either player, if they have not drawn yet, they can do a Mulligan
 * on that first turn and no other."*
 *
 * Three conditions, and only the first was ever a rule in this file:
 *   1. at most MULLIGAN_LIMIT per duel (the seat's own `mulliganUsed`);
 *   2. THE OPENING CYCLE ONLY — a seat's own first Turn;
 *   3. NOT YET DRAWN this Turn.
 *
 * (2) and (3) lived only in Shatterfield's local React state
 * (`myFirstTurnStarted` / `myFirstTurnOver` / `drawnThisTurn`), which
 * is UI, resets on remount, and is invisible to every headless driver
 * and to the gate. They are rules now.
 */
export const validateMulligan = (
  turnNumber,
  { mulligansUsed = 0, drawsThisTurn = 0, handSize = null, seatCount = 2 } = {}
) => {
  if ((mulligansUsed || 0) >= MULLIGAN_LIMIT) {
    return fail("R90", `One mulligan per duel (limit ${MULLIGAN_LIMIT})`);
  }
  if (!isOpeningTurn(turnNumber, seatCount)) {
    return fail("R90", "A mulligan is taken on the opening Cycle and no other Turn");
  }
  if ((drawsThisTurn || 0) > 0) {
    return fail("R90", "A seat that has already drawn this Turn may not mulligan");
  }
  // The hand must still be the DEALT hand — H15's reasoning, promoted
  // out of the handler. Slicing a fresh STARTING_HAND over a hand that
  // is no longer STARTING_HAND cards mints or eats a card.
  if (handSize != null && handSize !== STARTING_HAND) {
    return fail("R90", `A mulligan returns the dealt hand of ${STARTING_HAND}; this hand holds ${handSize}`);
  }
  return pass();
};

/**
 * R10 — Hand limit = HAND_LIMIT (rules.js; EIGHT, ruled canon by caP'n
 *        on 2026-08-26 — Rulez v2 says SEVEN and is superseded; this
 *        docstring said 7 for a year while the constant said 8).
 *        Drawing AT the cap still resolves; the discard follows.
 *        So this validates the hand AFTER the discard step.
 */
export const validateHandLimit = (hand) => {
  const size = (hand || []).length;
  if (size > HAND_LIMIT) {
    return fail("R10", `Hand size ${size} exceeds limit of ${HAND_LIMIT}`);
  }
  return pass();
};

/**
 * R12 — Natural Khao +1 only if NO Khao was spent that full Turn
 * R13 — Natural Khao max = MAX_KHAO_NAT (cards can push to MAX_KHAO_ABS)
 * R14 — Khao refills to maxKhao each Turn
 * R91 — No natural Khao gain on the opening Turn of the duel
 * R132 — and none before a full LAP has elapsed at a 3–4 seat table:
 *        the floor is rules.khaoClimbFloor(seatCount), the same number
 *        turnEngine.handOffHalfTurn grants on. Until 2026-09-15 this
 *        validator kept a two-seat approximation of that floor and would
 *        have blessed a climb the seam refuses at a bigger table.
 */
export const validateKhaoRefill = (currentKhao, maxKhao, spentThisTurn, turnNumber = null, gained = false, seatCount = 2) => {
  // R13 — the absolute ceiling, on both the pool and the cap.
  if (currentKhao > MAX_KHAO_ABS) {
    return fail("R13", `Khao ${currentKhao} exceeds absolute max of ${MAX_KHAO_ABS}`);
  }
  if (maxKhao > MAX_KHAO_ABS) {
    return fail("R13", `Max Khao ${maxKhao} exceeds ceiling of ${MAX_KHAO_ABS}`);
  }

  // R12 — the climb is bought with restraint. Spend anything and the
  // +1 does not come. This is now a REFUSAL rather than a comment
  // describing one.
  if (gained && spentThisTurn) {
    return fail("R12", "Natural Khao +1 only when NO Khao was spent that Turn");
  }

  // R91 / R132 — and it never comes before the climb floor: the opening
  // Turn at 1v1, a full lap of the table beyond it. ONE floor, read from
  // the catalogue, so this validator and the seam cannot disagree.
  if (gained && turnNumber != null && Number(turnNumber) < khaoClimbFloor(seatCount)) {
    return fail("R91", `No natural Khao gain before half-turn ${khaoClimbFloor(seatCount)} (R91 floor ${FIRST_KHAO_TURN}, lap ${halfTurnsPerFullTurn(seatCount)})`);
  }

  // R14 — the refill may never exceed the cap it refills to.
  if (currentKhao > maxKhao) {
    return fail("R14", `Khao ${currentKhao} exceeds this Turn's cap of ${maxKhao}`);
  }

  return pass();
};

/**
 * R15 — Khao cost must be paid before the card activates.
 * R96 — and a VOLUNTARY payment may never take the payer to zero, on
 *        whichever pool it lands on. canPayVoluntary is rules.js's one
 *        implementation of the till law; nothing re-derives it.
 * R107 — AND THE TWO POOLS NEVER MIX (caP'n, 2026-08-26: *"no mixing
 *        for any reason at all unless the card tells you"*).
 *
 * ⚠ WHAT THIS FUNCTION USED TO DO, AND WHY IT IS NOW WRONG. When the
 * caller passed `availableAnima` it computed `requiredKhao -
 * availableKhao` and asked whether ANIMA COULD COVER THE REMAINDER —
 * i.e. it validated a SPLIT payment. That is exactly the mixing R107
 * forbids. The Anima argument now means "the OTHER pool this fare may
 * be paid from, IN FULL", and the test is an either/or:
 *
 *     khao alone covers it   OR   anima alone covers it (R96-guarded).
 *
 * Passing `null` still means "Khao only", unchanged, and is what every
 * caller that does not name an alternative pool gets.
 */
export const validateKhaoPayment = (requiredKhao, availableKhao, availableAnima = null) => {
  const need = Number(requiredKhao) || 0;
  if (need <= 0) return pass();
  if (need <= (Number(availableKhao) || 0)) return pass();

  if (availableAnima == null) {
    return fail("R15", `Cannot afford ${need} Khao; only have ${availableKhao}`);
  }
  // R107 — the WHOLE fare out of Anima, or not at all. R96 guards the leg.
  if (canPayVoluntary(availableAnima, need)) return pass();
  return fail(["R15", "R107", "R96"],
    `Cannot pay ${need}: ${availableKhao} Khao is short and Anima cannot cover it whole — a fare is paid ENTIRELY in one pool (R107), and the Anima leg may never take you to 0 (R96)`);
};

// ═══════════════════════════════════════════════════════════
// TRANSCEND & ASCEND RULES
// ═══════════════════════════════════════════════════════════

/**
 * R16 — Transcend: L1 Escendant must match School of Escling
 * R17 — Can Transcend ALL Esclings in one turn (no limit)
 * R19 — Natural Ascend requires matching School AND Ilk
 * R20 — Natural Transcend/Ascend is free (no Khao cost)
 */
export const validateTranscend = (escling, escendantL1Card, allCards = []) => {
  if (!escling || !escendantL1Card) {
    return fail("R16", "No escling or L1 card provided");
  }

  if (escendantL1Card.type !== "Escendant" || escendantL1Card.level !== 1) {
    return fail("R16", "Transcend target must be a Level 1 Escendant");
  }

  if (escling.school !== escendantL1Card.school) {
    return fail(
      "R16",
      `Transcend: Escling School '${escling.school}' must match L1 School '${escendantL1Card.school}'`
    );
  }

  return pass();
};

/**
 * R18 — Only 1 Ascension per turn
 * R19 — Natural Ascend requires matching School AND Ilk
 * R20 — Natural Transcend/Ascend is free
 * R21 — Deviant Ascend costs Khao or Anima
 */
export const validateAscend = (
  fromLevel,
  toLevel,
  fromCard,
  toCard,
  isNatural,
  availableKhao,
  availableAnima,
  ascensionsThisTurn
) => {
  // Only one ascension per turn
  if (ascensionsThisTurn > 0) {
    return fail("R18", "Only 1 Ascension per turn");
  }

  // Ascending to next level
  if (toLevel !== fromLevel + 1) {
    return fail(
      "R19",
      `Can only Ascend to next level (${fromLevel + 1}), not ${toLevel}`
    );
  }

  // Natural ascend: must match both School and Ilk
  if (isNatural) {
    if (fromCard.school !== toCard.school || fromCard.ilk !== toCard.ilk) {
      return fail(
        "R19",
        `Natural Ascend: School and Ilk must match. From: ${fromCard.school}/${fromCard.ilk}, To: ${toCard.school}/${toCard.ilk}`
      );
    }
    // Natural is free (R20) — no cost validation needed
  } else {
    // ── R21 / R101 · THE DEVIANT FARE, FROM THE ONE TABLE ──────────
    // ⚠ THIS BRANCH USED TO BE THE THIRD OPINION IN A THREE-WAY
    // DISAGREEMENT, and it was the odd one out twice over:
    //   · It charged by `fromLevel` — the level being ascended FROM.
    //     Shatterfield's two helpers and NPC.js all charged by the
    //     DESTINATION. So a 0-trait L1→L2 climb cost 3 on the board and
    //     2 at this gate, and which number a seat paid depended on
    //     which door it came through.
    //   · It hardcoded the 0-trait row and could not see shared traits
    //     at all, so a 1-trait Ascend (unreachable with today's cards,
    //     but printed in the rules) was overcharged.
    // caP'n, 2026-08-26, on which level to read: Deviant Training's
    // level is the attached card's own and is unambiguous — read
    // Ascending the same way. So: the level being ascended TO.
    //
    // TWO NUMBERS. Rulez v2 ch.15's L3 / 0-trait cell is 5 Khao OR 4
    // Anima and the pools are checked against their own leg.
    const shared = (fromCard && toCard)
      ? ((fromCard.school === toCard.school ? 1 : 0) + (fromCard.ilk === toCard.ilk ? 1 : 0))
      : 0;
    const fare = deviantFare(shared, toLevel);
    // R96 — the Anima leg is a VOLUNTARY payment, so it is asked with
    // canPayVoluntary, not `<`. A bare `<` let a seat on exactly the
    // fare pay itself to 0; the Till Law says the action is refused,
    // not fatal. The two legs carry different numbers here, which is
    // why this is not one farePoolsFor call.
    if (availableKhao < fare.khao && !canPayVoluntary(availableAnima, fare.anima)) {
      return fail(
        "R21",
        `Deviant Ascend costs ${fare.khao} Khao or ${fare.anima} Anima; only have ${availableKhao} Khao, ${availableAnima} Anima`
      );
    }
  }

  return pass();
};

// ═══════════════════════════════════════════════════════════
// COMBAT RULES
// ═══════════════════════════════════════════════════════════

/**
 * R22 — Lyt >= Lyf -> Fracture (Lyf drops to 0)
 * R23 — Fractured L1 -> Ruined and sent to Necropolis on next hit
 * R24 — Fractured L2/L3 -> Descends to prev level, Fallen state
 * R25 — Fallen Escendants are IMPERVIOUS to Lyt Attacks
 * R26 — Fallen beings shield for ONE turn after descent, then become bypassable to Anima
 * R27 — Chip damage STANDS through the rest of the attacker's Turn and
 *       through the victim's own whole Turn; a barrage accumulates
 * R28 — Lyf refreshes at the end of its OWN seat's Turn, for that seat's
 *       beings only (caP'n, 2026-08-26: "the being's Lyf stays chipped
 *       or even in a fractured state until its own turn")
 * R29 — 0-Lyf Escendants are always Fractured, can be ruined immediately
 * R30 — Each Escendant can only attack once per turn
 */
export const validateAttack = (
  attacker,
  defender,
  attackingSlotIndex,
  defendingSlotIndex,
  lytDamage,
  defenderAnima,
  defendersAttackedThisTurn,
  allCards = [],
  rifts = null,          // R119 — the DEFENDER's battle line
  attackerRifts = null,  // R121 — the ATTACKER's own
  currentTurn = null,    // R115 — the Roles sleep for three full Turns
  seatCount = 2
) => {
  const violations = [];

  // ── R119 · THE WARDEN SHIELD, AND R121 · THE MIND'S SILENCE ────
  // Both are questions about WHERE a being is standing, so both need the
  // whole battle line rather than the two beings in the swing — which is
  // why `rifts` and `attackerRifts` were added to this signature. They are
  // OPTIONAL and default to null: a caller that has not been taught yet
  // gets exactly the pre-2026-09-07 behaviour rather than a crash, and
  // every stored duel and replay validates as it always did.
  //
  // ⚠ THE DEFENDER'S SHIELD IS ASKED BEFORE R25's Fallen clause, because
  // it is a broader refusal: an unreachable Warden is unreachable whatever
  // state it is in, and a player who is told "that being is Fallen" about a
  // being that is actually shielded has been told the wrong reason.
  if (Array.isArray(rifts) && defendingSlotIndex === MIND_RIFT
      && !wardenOpenToLytAttack(rifts, currentTurn, seatCount)) {
    return fail("R119", "That Warden cannot be Lyt-attacked while its Body rift still stands. Ruin the Body and leave it Fallen, or leave nothing but Esclings beside it — every other kind of Attack reaches a Warden freely");
  }

  // ⚠ AND THE ATTACKER'S OWN SILENCE. A being in the Mind rift may perform
  // NOTHING while its seat's Body, Sol and Heart all hold Escendants — the
  // price the Warden pays for being unreachable. A Lyt Attack is the most
  // obvious thing it may not do, and refusing it here rather than in the
  // renderer is what makes the ban bind the brain too.
  if (Array.isArray(attackerRifts) && attackingSlotIndex === MIND_RIFT
      && !mindMayAct(attackerRifts, "ATTACK", currentTurn, seatCount)) {
    return fail("R121", "Your Body, Sol and Heart rifts all hold Escendants, so your Mind may perform nothing but its own Warden Art");
  }

  // R25 — Can't Lyt attack a Fallen being
  if (isFallenBeing(defender)) {
    return fail("R25", "Cannot Lyt-attack a Fallen Escendant; it is impervious");
  }

  // R29 — 0-Lyf Escendants are always Fractured.
  // ⚠ THIS USED TO BE A CHECK, AND THE CHECK COULD NEVER FIRE. It read
  // `defender.lyf === 0 && !isFractured(defender)`, and isFractured is
  // DEFINED as `lyf === 0` (see the helper at the top of this file) — so
  // the conjunction was a contradiction and validateAttack has never once
  // returned R29 in the life of this codebase. A dead branch wearing a
  // rule code is worse than no branch: it reads as coverage.
  //
  // R29 is not a prohibition. It is a DEFINITION — "0 Lyf means Fractured,
  // and a Fractured being can be ruined immediately" — and definitions are
  // enforced by the thing that computes them, not by a validator refusing
  // an action. isFractured() IS R29. The rule is honoured above, where R25
  // refuses an attack on a FALLEN being and pointedly does not refuse one
  // on a Fractured being, which is exactly what "can be ruined immediately"
  // means. Nothing to check here; saying so out loud instead of leaving a
  // branch that lies about it.

  // ── R30 + R124 — ONE SWING, UNLESS A CARD PRINTS MORE ────────
  // ⚠ THIS USED TO BE `> 0`, FULL STOP, and two shipped cards print an
  // exception to it: King of the Anthill grants a swing per other
  // Antdroid in combat, Sword of Shadows grants its wielder one. Both
  // were dead — see R124's banner for how dead.
  //
  // The allowance is asked of `rules.lytAttacksAllowed`, which reads the
  // attacker's own slot and both battle lines. `attackerRifts` is
  // OPTIONAL on this signature (a caller that has not been taught passes
  // null), so an unaware caller gets the old answer of exactly one —
  // the strict direction, and the same compatibility shape R119 and
  // R121 already use two blocks above.
  const thisSlotAttacks = defendersAttackedThisTurn.filter(idx => idx === attackingSlotIndex).length;
  const allowed = lytAttacksAllowed(
    (attackerRifts || [])[attackingSlotIndex] || attacker,
    attackerRifts || [], rifts || [], (id) => getCard(id, allCards), attackingSlotIndex
  );
  if (thisSlotAttacks >= allowed) {
    return fail("R30", allowed > 1
      ? `This Escendant has taken all ${allowed} of its Lyt Attacks this turn`
      : "This Escendant has already attacked this turn");
  }

  // R22 — Lyt >= Lyf = Fracture
  if (lytDamage >= defender?.lyf) {
    // Expect Lyf to become 0 after this attack
    // Validator cannot enforce the state change, but can flag if post-attack it wasn't enforced
  }

  return violations.length ? fail(violations) : pass();
};

/**
 * R31 — THE WALL OF BEINGS STOPS **LYT ATTACKS** ONLY.
 *       You may only LYT-ATTACK Anima if every opponent Escendant is
 *       gone or Fallen. Fractured beings still block; Esclings never do.
 * R32 — Anima cannot exceed 50
 * R33 — Anima at 0 = loss
 *
 * ── caP'n, 2026-08-26, verbatim ────────────────────────────────────
 *   *"the wall of beings only stops dimming of Anima from Lyt Attacks
 *   (excluding fallen states etc) — precisely, it needs 'Lyt' in it,
 *   you said it, I must've just forgot it."*
 *
 * Rulez v2, Chapter 6 ("Lyt Attacks Against Anima"), corroborating:
 *   *"If your adversary has no Escendants standing (only Esclings,
 *   Fallen beings, or empty rifts), you may Lyt-attack their Anima
 *   directly. ... If even a single standing Escendant remains (not
 *   Fallen), Anima cannot be targeted directly."*
 * The whole chapter is titled for the swing, and the sentence that
 * bars the route is a sentence about Lyt-attacking.
 *
 * ⚠ THIS FUNCTION IS ONLY EVER CALLED ON THE SWING, and that is the
 * ruling being obeyed rather than a coincidence. ruleGate.gateAnimaAttack
 * is its one door and Shatterfield asks it from exactly one place — the
 * Escendant-vs-Anima Lyt Attack handler. A Tome, an Impera, an U'hlti, a
 * Curio or a Primera stamp that dims Anima reaches through a full board
 * of standing beings and asks NOTHING here. If a card branch ever starts
 * calling this, the ruling has been broken in the expensive direction:
 * cards would silently stop working behind a wall.
 */
export const validateAnimaAttack = (field, targetAnima, defenderAnima, currentTurn = null, hasDirectAnima = false, seatCount = 2) => {
  // ── R08 · AND THIS PARAMETER USED TO BE DECORATION ────────────
  // `currentTurn` was declared, documented, threaded all the way from
  // ruleGate through validateAction's ATTACK_ANIMA case — and READ BY
  // NOTHING. So R08 refused a Lyt Attack on the opening Cycle and let
  // an ANIMA STRIKE through, which is the same rule pointed at half
  // the game. An unread parameter is worse than a missing one: it
  // reads as coverage, and every caller was already paying to pass it.
  //
  // FIRST, and before the Phantom Paws bypass. `hasDirectAnima` buys
  // you past R31 (the standing-being wall) — it does not buy you past
  // R08, because R08 is not about what is standing in the way. That
  // ordering is the whole fix for the Phantom stamp's direct dim.
  if (currentTurn != null && isOpeningTurn(currentTurn, seatCount)) {
    return fail("R08", "No Attacks of any kind on the opening Cycle — an Anima strike is an Attack");
  }

  // Phantom Paws (canDirectAnima) bypasses the standing-being requirement.
  if (hasDirectAnima) return pass();

  // R26 (canon, July 2026): Fallen beings are BYPASSABLE IMMEDIATELY —
  // a being that descended, even this very turn, does NOT block Anima.
  // R31 (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27): FRACTURED STANDS, FRACTURED BLOCKS.
  // Lyf is irrelevant to blocking — a Fractured (0-Lyf) Escendant still
  // guards the Anima. The ONLY bypass is the Fallen state. A being blocks
  // if: it's an Escendant (esclings don't block) and it is NOT Fallen.
  //
  // ⚠ AND R119 ADDED A SECOND BYPASS ON 2026-09-07: the MIND rift. caP'n:
  // *"You dont need to ruin the warden or leave it in a fallen state to
  // dim Anima, only the other 3"* — so for this one question a Warden is
  // not on the board at all. The predicate is now per-RIFT rather than a
  // flat `.some`, because whether a being blocks depends on WHERE it is
  // standing and no longer only on what state it is in.
  //
  // ⚠ THIS FUNCTION HAS A TWIN IN THE RENDERER (Shatterfield's
  // hasStandingBeing) AND THE TWO MUST MOVE TOGETHER. If one learns R119
  // and the other does not, the UI and the gate disagree: one offers the
  // swing and the other refuses it, which reads to a player as a dead
  // button with no explanation. Both now ask the same exported predicate.
  // ⚠ THIS ASKS THE EXPORTED PREDICATE RATHER THAN RESTATING IT, and the
  // first cut of this block did restate it — a fourth private copy of the
  // wall rule, in the gate, under a comment claiming both sides asked the
  // same thing. That is trap 4.5 exactly: the second workaround for one
  // inconsistency, correct and documented and still a compensation. The
  // only local work left is normalising this file's `__raw`/`isFallen`
  // slot wrapper into the plain shape the catalogue speaks.
  const blocking = (field?.esclings || []).some((s, i) => {
    const raw = s?.__raw || s;
    const plain = { escendant: raw?.escendant, fallen: !!(s?.isFallen || raw?.fallen) };
    return blocksAnimaLytAttack(plain, i, currentTurn, seatCount);
  });

  if (blocking) {
    return fail("R31", "Cannot attack Anima while opponent has a standing Escendant");
  }

  // ⚠ R32 USED TO BE GUARDED HERE, BACKWARDS. The removed code was:
  //     if (targetAnima && defenderAnima + targetAnima > MAX_ANIMA)
  //         return fail("R32", ...)
  // An Anima attack REDUCES the defender's Anima. Adding the damage to
  // their pool and refusing the strike when the sum passes 50 makes a
  // seat that has healed near the ceiling IMMUNE to being hit — the
  // healthier you are, the harder you are to damage, which is the exact
  // opposite of the rule. The comment on that line even said "defensive;
  // Anima attacks reduce, don't gain" while the code did the adding.
  //
  // It never bit because the one caller on the shipping path
  // (ruleGate.gateAnimaAttack -> Shatterfield's Anima strike) hardcodes
  // targetAnima: 0, which also made the branch dead code. It was one
  // caller away from live, so it comes out rather than staying as a trap.
  //
  // R32 belongs on the HEALING path, and that is where it is enforced —
  // every Anima gain clamps with Math.min(MAX_ANIMA, ...) and
  // validateAnimaGain below is the seal for it.

  return pass();
};

/**
 * R32 — Anima cannot exceed 50.
 *
 * The seal for the HEALING path. It was previously enforced only by a
 * scattering of Math.min(MAX_ANIMA, ...) at ~40 gain sites and by a
 * backwards guard on the ATTACK path (removed above, see the banner
 * there). Neither is a rule: one is arithmetic that each site has to
 * remember, the other was the rule pointed the wrong way. This is the
 * one place that can be asked, and the one place a mutation can redden.
 *
 * `proposed` is the Anima the seat would hold AFTER the gain. Returns a
 * pass with the clamped value so callers can use it directly instead of
 * re-deriving the clamp.
 */
export const validateAnimaGain = (currentAnima, amount) => {
  const have = Number(currentAnima) || 0;
  const gain = Number(amount) || 0;
  if (gain < 0) return fail("R32", "A gain cannot be negative — that is a dim, and R33 owns it");
  const proposed = have + gain;
  if (proposed > MAX_ANIMA) {
    // Not a refusal: the gain is legal, the OVERFLOW is not. R32 caps, it
    // does not forbid — a seat on 48 drinking 5 lands on 50 and keeps the
    // 3 it could not hold. Callers that treat this as a refusal are wrong.
    return { ...pass(), clamped: true, value: MAX_ANIMA, spilled: proposed - MAX_ANIMA };
  }
  return { ...pass(), clamped: false, value: proposed, spilled: 0 };
};

/**
 * R33 — Anima at 0 = loss.
 *
 * ⚠ THIS RETURNED `{ gameOver, loser, reason }` — WHICH IS NOT A VERDICT.
 * Every other function in this file returns `{ valid, violations, reason }`
 * and `validateAction` / `ruleGate.enforce` can consume nothing else, so
 * R33's named enforcement could not be routed through the switch, could
 * not be wrapped by a gate, and could not be reddened by a mutation even
 * if somebody had called it — and nobody ever did. It answers in the
 * house shape now, and it keeps the old three fields BESIDE the verdict
 * so a caller that wants the plain English question ("is this seat out?")
 * still gets it without a second function.
 *
 * ⚠ AND R96 IS THE REASON A REFUSAL HERE IS NOT A CONTRADICTION. A seat
 * may not PAY itself to zero — `canPayVoluntary` refuses that at the till
 * — but an adversary's damage takes it to zero cheerfully, and this is
 * where that state is named. A board sitting at 0 with the duel still
 * running is the illegal thing, which is exactly what GameStateValidator's
 * S04 watches from the other side.
 */
export const validateGameEnd = (anima) => {
  const a = Number(anima) || 0;
  if (a <= 0) {
    return {
      ...fail("R33", "Anima reached 0 — that seat has lost the Solrend (R33)"),
      gameOver: true, loser: true,
    };
  }
  return { ...pass(), gameOver: false, loser: false };
};

// ═══════════════════════════════════════════════════════════
// TOME RULES
// ═══════════════════════════════════════════════════════════

/**
 * R34 — Tomes placed face-down, no cost to place
 * R35 — L1 Tome: must wait 1 full turn after placing to activate
 * R36 — L2 Tome: can activate on opponent's turn after placing
 * R37 — L3 Tome: can activate from hand on opponent's turn
 * R38 — Max 3 Tome slots on field
 * R39 — Tomes ruined after use (unless OPT/OP2/OP3/ATV)
 */
export const validateTomePlacement = (tomes) => {
  const count = (tomes || []).filter(t => t).length;
  if (count > TOME_SLOTS) {
    return fail("R38", `Max ${TOME_SLOTS} Tome slots, have ${count}`);
  }
  return pass();
};

/**
 * R35 — L1 Tome wait validation
 */
export const validateTomeActivation = (tome, turnsOnField, allCards = [], opts = {}) => {
  if (!tome) return fail("", "No tome provided");

  const card = getCard(tome.id, allCards);
  if (!card) return fail("", "Tome card not found");

  // ── R35 · THE WAIT IS A PRICE ON SETTING, NOT ON THE CARD ────
  // caP'n, 2026-09-13: *"Yes we can, but if you place it face down, you
  // must wait 1 full turn to activate it."* A Tome played FACE-UP from
  // hand is being activated, not set, and waits for nothing.
  //
  // ⚠ THIS USED TO CHARGE THE WAIT OFF `card.level` ALONE, and
  // Shatterfield's hand-play path asks with `turnsOnField: 0` — so an
  // L1 Tome played face-up from hand was refused outright, against
  // Chapter 13's own sentence. `faceDown` is read off the Tome when it
  // carries the flag (a rift Tome does) and defaults to TRUE otherwise,
  // which is the strict direction.
  const faceDown = opts.faceDown != null ? !!opts.faceDown
    : (tome.faceDown != null ? !!tome.faceDown : true);
  const wait = tomeWaitHalfTurns(card.level, { faceDown, seatCount: opts.seatCount || 2 });

  if (turnsOnField < wait) {
    return fail(
      `R${34 + card.level}`,
      `A face-down L${card.level} Tome must wait a full Turn before it may flip (${turnsOnField} of ${wait} half-turns elapsed)`
    );
  }

  // ── R122 · THE CADENCE TAG IS A LAW, NOT DECORATION ──────────
  // caP'n, 2026-09-13: *"every card, acronyms like OPT OP3 etc are real,
  // they are not just there to look cool."* RULEZ_V2 ch.18: the tag binds
  // every card that prints one — a Tome, a Curio, an Impera, an U'hlti,
  // an Affinity.
  //
  // ⚠ THIS LIMB WAS MISSING AND THE RULE WAS LIVING IN THE RENDERER.
  // The only thing asking a Tome's cadence was the `legal` flag on a
  // radial wedge in Shatterfield.js (its own comment says "R122's cadence
  // is what gates this wedge"). A flag on a button is not a rule: the
  // brain never sees it, a replay never sees it, and a second call site
  // that forgets to grey the button gets a free second firing. The same
  // question is asked HERE now, out of the same `cooldownHalfTurns` the
  // board and the brain both call, so the three cannot disagree.
  //
  // `lastUsedTurn` and `turn` are both HALF-turn counters (`match.turn`),
  // and `cooldownHalfTurns` converts the FULL-turn tag into that unit —
  // OPT = 2 half-turns at a duel, 4 at a 2v2. Never retype the number.
  //
  // ⚠ ABSENT `lastUsedTurn` MEANS NEVER FIRED, AND THAT IS THE
  // COMPATIBILITY HINGE. Every Tome written before the board began
  // stamping `lastUsedTurn` has no such field, and reading that as 0
  // would refuse it for the first OPT window of every stored duel. A
  // Tome that has never fired is off cooldown, which is also the answer
  // the renderer's isTomeOffCooldown gives.
  // ⚠ AN ABSENT TAG STILL GETS THE FALLBACK, BECAUSE THE BOARD GIVES IT
  // ONE. `cooldownHalfTurns` returns one full Turn for an unknown or
  // missing tag — its own banner calls that "the safe direction: a
  // mislabelled card recurs too slowly rather than every half-turn" —
  // and Shatterfield's isTomeOffCooldown passes `card?.effectTag`
  // straight in, so an untagged Tome is already held for a full Turn on
  // the shipping board. Skipping the fallback here would have made the
  // gate LOOSER than the board on nine of the 165 swept cases, which is
  // a balance change in a coherency costume. It is unreachable in
  // practice — an untagged Tome ruins after use (R39) and takes its
  // `lastUsedTurn` out of the rift with it — and matching the board is
  // free, so the gate matches the board.
  const last = tome.lastUsedTurn;
  if (last != null && opts.turn != null) {
    const now  = Math.floor(Number(opts.turn)) || 0;
    const then = Math.floor(Number(last)) || 0;
    const cd   = cooldownHalfTurns(card.effectTag, opts.seatCount || 2);
    if (now - then < cd) {
      return fail(
        "R122",
        `${card.name}${card.effectTag ? ` is ${card.effectTag}` : ""} — it fired on half-turn ${then} and may fire again at ${then + cd} (it is ${now})`
      );
    }
  }

  return pass();
};

/**
 * R39 — Tomes ruin after use, unless they carry a persistent tag
 *        (PERSISTENT_TOME_TAGS in rules.js: OPT/OP2/OP3/ATV).
 *
 * ⚠ THIS RETURNED pass() ON BOTH BRANCHES. It was exported, listed in
 * Validators.Tomes.Ruin, and enforced NOTHING — which also left B02
 * ("non-persistent Tome remained on field after activation") with no
 * anchor but its own regression helper. It answers the question now:
 * given a Tome that has been used, is it still on the field?
 *
 * `hasOpt` may be omitted — the tags are read off the card, which is
 * the only copy of that list anyone should be consulting.
 */
export const validateTomeRuin = (tome, hasOpt = null, stillOnField = null, allCards = []) => {
  if (!tome) return pass(); // nothing was used; nothing to ruin

  const card = getCard(tome.id, allCards);
  const tags = (card && card.tags) || tome.tags || [];
  const persistent = hasOpt === null
    ? tags.some(t => PERSISTENT_TOME_TAGS.includes(t))
    : !!hasOpt;

  // R39 exception — a tagged Tome is SUPPOSED to still be there.
  if (persistent) return pass();

  const onField = stillOnField === null ? !!tome.onField : !!stillOnField;
  if (onField) {
    return fail(
      ["R39", "B02"],
      `Tome '${(card && card.name) || tome.id}' has no persistent tag (${PERSISTENT_TOME_TAGS.join("/")}) and must ruin after use, but is still on the field`
    );
  }
  return pass();
};

// ═══════════════════════════════════════════════════════════
// GRAVESHIFT RULES
// ═══════════════════════════════════════════════════════════

/**
 * R40 — Graveshift ONLY when Grimoire is completely empty
 * R41 — Graveshift costs exactly 10 Anima
 * R42 — Graveshift combines hand + Necropolis into new Grimoire
 * R43 — Cannot lose by running out of cards (Graveshift exists)
 */
export const validateGraveshift = (
  grimoire,
  necropolis,
  hand,
  anima,
  availableAnima
) => {
  // R40 — Grimoire must be empty. Handle both array and {id:qty} map shapes.
  const grimoireCount = Array.isArray(grimoire)
    ? grimoire.length
    : (grimoire && typeof grimoire === "object"
        ? Object.values(grimoire).reduce((a, b) => a + (b || 0), 0)
        : 0);
  if (grimoireCount > 0) {
    return fail("R40", "Graveshift only when Grimoire is completely empty");
  }

  // R41 — Costs exactly GRAVESHIFT_COST
  if (availableAnima < GRAVESHIFT_COST) {
    return fail(
      "R41",
      `Graveshift costs ${GRAVESHIFT_COST} Anima; only have ${availableAnima}`
    );
  }

  // R42 — Sanity check that hand + necropolis are non-empty
  const totalCards = (hand || []).length + (necropolis || []).length;
  if (totalCards === 0) {
    return fail(
      "R42",
      "Cannot Graveshift with empty hand and necropolis (would draw from nothing)"
    );
  }

  return pass();
};

// ═══════════════════════════════════════════════════════════
// RESURRECTION RULES
// ═══════════════════════════════════════════════════════════

/**
 * R44 — Resurrection TALLIES (Rulez July 25, 2026 wave): per Solrend,
 *        per card instance — Bebs 3× · Champions 2× · Lords 1×.
 *        EVERY method counts toward the tally, no matter the source —
 *        paid Resurrection, card effect, Tome, the Time Primera.
 * R45 — Must resurrect in order — L1 first, then L2, then L3
 * R46 — Cannot skip levels (no L3 directly onto Escling)
 * R47 — Cost: L1=2 Khao/Anima, L2=4 Khao/Anima, L3=4 Khao/Anima
 *        (skipped when `free` — a no-cost method still marks the tally)
 * R48 — Resurrected Escendants keep their Souls
 *
 * `resurrectionsThisSolrend` is the CARD INSTANCE's tally COUNT (how many
 * times this exact card id has come back this Solrend) — no longer a
 * binary. Callers count occurrences in the append-only resurrectedCards.
 */
export const validateResurrection = (
  escling,
  fromLevel,
  toLevel,
  availableKhao,
  availableAnima,
  resurrectionsThisSolrend,
  necropolis,
  cardName,
  free = false,
  // R86 — `passive` marks the PAID Necropolis rite. Card effects leave
  // it false and are never gated by the Graveshift generation.
  passive = false,
  graveshifted = false
) => {
  // R44/R85 — per-COPY tally by rung (every method counts)
  // ── R86 — THE GRAVESHIFT DOOR (caP'n, July 27 2026) ──────
  // The PASSIVE rite (the paid Necropolis Resurrect) is closed to any
  // card that has been through a Graveshift. Card effects — Tomes,
  // Impera, the Time Primera — are NOT gated and must not pass
  // `passive`, which is why this only fires when both flags are set.
  if (passive && graveshifted) {
    return fail(
      "R86",
      `${cardName || "That being"} has been through a Graveshift — only card effects can Resurrect it now`
    );
  }

  // R85 — the tally is PER COPY. Callers count occurrences of this
  // copy's seal in the append-only resurrectedCards (see necroSeal.js).
  const limit = RESURRECT_LIMIT[`L${toLevel}`] ?? 1;
  if ((resurrectionsThisSolrend || 0) >= limit) {
    return fail(
      "R44",
      `${cardName} has reached its Resurrection tally (${limit}× per Solrend)`
    );
  }

  // R45, R46 — Level order
  if (toLevel !== fromLevel + 1) {
    return fail(
      ["R45", "R46"],
      `Must resurrect in order: cannot skip from L${fromLevel} to L${toLevel}`
    );
  }

  // R47 — Cost check (free methods — Time Primera, card effects — skip
  // the fare but never the tally above)
  if (!free) {
    const cost = RESURRECT_COST[`L${toLevel}`];
    if (!cost) {
      return fail("R47", `Unknown resurrection level L${toLevel}`);
    }

    // R96 — the Anima leg of a Resurrection fare is voluntary and may
    // never take the seat to 0. farePoolsFor asks both pools with the
    // Till Law already inside it.
    if (farePoolsFor(cost, { khao: availableKhao, anima: availableAnima }).length === 0) {
      return fail(
        "R47",
        `Resurrection to L${toLevel} costs ${cost} Khao or Anima; have ${availableKhao} Khao, ${availableAnima} Anima`
      );
    }
  }

  return pass();
};

// ═══════════════════════════════════════════════════════════
// PRIMERA RULES — THE STAMPS (Rulez July 25, 2026 wave)
// ═══════════════════════════════════════════════════════════

/**
 * R78 — Every Escendant bears 2 Primeras — its Ilk's and its School's —
 *        innate stamp powers, not cards. Esclings bear none.
 * R79 — Cost = the being's Level (L1=1, L2=2, L3=3), paid ENTIRELY in
 *        Khao or ENTIRELY in Anima. NOT mixable — R107, ruled
 *        2026-08-26. This validator combined the two pools until then,
 *        and it was the only place in the enforcer that did.
 * R80 — Own Turn only — NEVER in a Response Window, no interrupts.
 * R81 — One Primera per BEING per Turn (its Ilk or its School, never both).
 * R82 — One use per WORD per Turn across the whole field — Dragon is
 *        ONE word on both axes.
 * (R83 stacking and R84 of-the-flesh are structural — nothing to gate.)
 *
 * `axis` is the axis ("ilk" | "school") keying rules.js PRIMERAS, and
 * since the 2026-08-25 data fix it is also the field name on the card:
 * `card.ilk` is Humanoid/Beast/Machine/Dragon and `card.school` is
 * Space/Phantom/Dragon/Time. The inversion note that stood here — and
 * the caller-side mapping it described — is gone with it.
 */
export const validatePrimera = (
  word,
  axis,
  level,
  availableKhao,
  availableAnima,
  wordUsedThisTurn,
  beingUsedThisTurn,
  isOwnTurn,
  inResponseWindow
) => {
  // R78 — the word must be one of the eight stamps on its canon axis
  const stamp = PRIMERAS?.[axis]?.[word];
  if (!stamp) {
    return fail("R78", `${word} is not a ${axis === "ilk" ? "Ilk" : "School"} Primera`);
  }

  // R80 — own Turn only, never through a Response Window
  if (!isOwnTurn) {
    return fail("R80", "Primeras fire on your own Turn only");
  }
  if (inResponseWindow) {
    return fail("R80", "Primeras never fire in a Response Window — no interrupts");
  }

  // R81 — one stamp per being per Turn
  if (beingUsedThisTurn) {
    return fail("R81", "This being already fired a Primera this Turn");
  }

  // R82 — one use per WORD per Turn across the whole field
  if (wordUsedThisTurn) {
    return fail("R82", `${word} has already been spoken this Turn`);
  }

  // R79 + R107 — cost = Level, paid out of ONE pool. `farePoolsFor` is
  // rules.js's single implementation of that question and carries R96's
  // till law on the Anima leg, so nothing is re-derived here.
  const cost = PRIMERA_COST[`L${level}`];
  if (!cost) {
    return fail("R79", `Unknown Primera level L${level}`);
  }
  const pools = farePoolsFor(cost, { khao: availableKhao || 0, anima: availableAnima || 0 });
  if (pools.length === 0) {
    return fail(
      ["R79", "R107"],
      `${word} costs ${cost}, paid ENTIRELY in Khao or ENTIRELY in Anima (R107 — no mixing); have ${availableKhao || 0} Khao and ${availableAnima || 0} Anima`
    );
  }

  return pass();
};

// ═══════════════════════════════════════════════════════════
// SOUL & ATTACHMENT RULES
// ═══════════════════════════════════════════════════════════

/**
 * R49 — Soul = all cards attached to an Escendant
 * R50 — When L1 ruined, Soul sent to Necropolis
 * R51 — When L2/L3 descends, only the ruined level leaves the being
 * R52 — Every attached card (Impera at EVERY level, U'hlti, Affinity)
 *       survives the descent; nothing attached is level-bound
 *       (caP'n, 13 Sept 2026 — overturns the July "level counterpart"
 *       reading; RULEZ_V2 :541, :563)
 * R71 — Escendant can only have 1 Impera of each level
 * R72 — Escendant can only have 1 U'hlti or Tome attached
 * R73 — Escendant can only have 1 Affinity attached
 */
/**
 * R122 — may this already-attached card fire on this half-turn?
 *
 * The cadence lives on the CARD (`effectTag`) and the stamp lives on
 * the BEING (`slot.soulUsed[cardId]`), counted in the same half-turns
 * `match.turn` counts, through the same `cooldownHalfTurns` a Tome
 * uses. An untagged card is a passive — it is read, never pressed —
 * and `cooldownHalfTurns` falls back to one full Turn for it, so a
 * card that acquires a tag tomorrow is gated the moment it does.
 */
export const validateSoulActivation = (
  being,
  soulCardId,
  currentTurn = 0,
  { seatCount = 2, allCards = [] } = {}
) => {
  if (!soulCardId) return fail("", "No soul card named");
  const card = getCard(soulCardId, allCards);
  if (!card) return fail("", `Soul card ${soulCardId} not found`);
  if (!(being?.soul || []).includes(soulCardId)) {
    return fail("R49", `${card.name || soulCardId} is not attached to that being`);
  }
  if (!soulCardOffCooldown(being, soulCardId, card.effectTag, currentTurn, seatCount)) {
    return fail("R122", `${card.name || soulCardId} is ${card.effectTag || "OPT"} — it has already acted this cadence`);
  }
  return pass();
};

export const validateSoulAttachment = (
  being,
  newAttachment,
  allCards = []
) => {
  if (!being || !newAttachment) {
    return fail("", "No being or attachment provided");
  }

  const attachCard = getCard(newAttachment, allCards);
  if (!attachCard) {
    return fail("", `Attachment ${newAttachment} not found`);
  }

  const soul = being.soul || [];

  // R73 — Only 1 Affinity
  if (attachCard.type === "Affinity") {
    if (hasAffinity(being)) {
      return fail("R73", "Escendant can only have 1 Affinity");
    }
  }

  // R72 — Only 1 U'hlti or Heretical Tome
  if (attachCard.type === "U'hlti" || (attachCard.type === "Tome" && attachCard.id.startsWith("ht_"))) {
    if (hasUhlti(being) || hasHereticalTome(being)) {
      return fail("R72", "Escendant can only have 1 U'hlti or Heretical Tome");
    }
  }

  // ── R70 · AN U'HLTI ATTACHES AT LEVEL 3, AND ONLY TO A NATURAL ──
  // RULEZ_V2 ch.16, verbatim: *"It attaches only when all of this is
  // true: … The being is **Natural** — no Deviant Transcend, Ascend or
  // Training, no Heretical Training, ever. **The being is Level 3.** Not
  // Level 1, not Level 2."*
  //
  // ⚠ THE BOARD HAS ALWAYS REFUSED THIS AND THIS VALIDATOR NEVER ASKED.
  // Shatterfield's handleAttachSoulCard refuses both by hand — "U'hltis
  // can only attach to Level 3 beings" and "Only Natural Escendants can
  // have U'hltis" — so the shipping game is right and the GATE was the
  // half that could not see it. Nothing about what the game does changes
  // here; what changes is that the rule is now asked in the one place
  // every driver passes through, so the brain and any future headless
  // seat are bound by it too. validateAffinityAttachment has carried the
  // same two clauses for the Affinity since July; the U'hlti — the harder
  // requirement, since the Affinity cannot be worn without it — had
  // neither.
  if (attachCard.type === "U'hlti") {
    if (!isNaturalBeing(being)) {
      return fail("R70", "An U'hlti attaches only to a NATURAL Escendant — a Deviant being never wears one");
    }
    if (getBeingLevel(being) !== 3) {
      return fail("R70", `An U'hlti attaches at Level 3 only — that being is Level ${getBeingLevel(being)}`);
    }
  }

  // R71 — Only 1 Impera per level per being
  if (attachCard.type === "Impera") {
    const level = attachCard.level;
    const hasImperaOfLevel = soul.some(id => {
      const soulCard = getCard(id, allCards);
      return soulCard && soulCard.type === "Impera" && soulCard.level === level;
    });
    if (hasImperaOfLevel) {
      return fail("R71", `Escendant already has a L${level} Impera`);
    }
  }

  return pass();
};

/**
 * R70 — Affinity only attachable when Natural L3 has U'hlti
 */
export const validateAffinityAttachment = (being, allCards = []) => {
  if (!isNaturalBeing(being) || getBeingLevel(being) !== 3) {
    return fail("R70", "Affinity can only attach to a Natural L3 Escendant");
  }

  if (!hasUhlti(being)) {
    return fail("R70", "Affinity requires a U'hlti to be attached first");
  }

  return pass();
};

// ═══════════════════════════════════════════════════════════
// DEVIANT & HERETICAL RULES
// ═══════════════════════════════════════════════════════════

/**
 * R64 — Deviant Escendants cannot equip U'hltis or Affinities
 * R65 — Deviant Transcend costs nothing but marks Deviant
 * R66 — Only 1 Deviant Ascend per turn
 * R67 — Heretical Training: 6 Khao + 6 Anima, L3 Deviant only, no U'hlti present
 * R68 — Heretical Tome not part of Soul (doesn't return on Resurrect)
 */
export const validateDeviancyRestrictions = (being) => {
  if (!being || !being.isDeviant) return pass();

  if (hasUhlti(being) || hasAffinity(being)) {
    return fail(
      "R64",
      "Deviant Escendants cannot have U'hltis or Affinities"
    );
  }

  return pass();
};

/**
 * R67 — Heretical Training
 */
export const validateHereticalTraining = (
  being,
  availableKhao,
  availableAnima,
  allCards = []
) => {
  // Must be L3
  if (getBeingLevel(being) !== 3) {
    return fail("R67", "Heretical Training target must be a L3 Escendant");
  }

  // ⚠ THE `isDeviant` REQUIREMENT IS GONE, 2026-08-20, AND IT IS A
  // RULEBOOK RECONCILIATION RATHER THAN A LOOSENING.
  //
  // Chapter 18 contradicted itself in three places: the prose said "a
  // Level 3 DEVIANT being", the same chapter's Distinctions panel said
  // the rite "may be performed on Natural beings too ... but marks the
  // being as Deviant, permanently", and the glossary said only "a
  // Level 3 being". This validator had implemented the strictest of the
  // three, which made the Distinctions panel unreachable — a published
  // paragraph describing an action the enforcer would refuse.
  //
  // The reading that makes ALL THREE true at once:
  //
  //     THE TARGET IS ANY LEVEL 3 WITH AN EMPTY U'HLTI SLOT.
  //     THE RITE MAKES IT DEVIANT.
  //
  // — so afterwards it is always a Deviant carrying the Tome (the
  // prose), a Natural may be targeted and pays for it forever (the
  // panel), and the only stated requirement is the level (the
  // glossary). The Rulebook has been edited to say this in one voice
  // and R67's catalog row with it.

  // No U'hlti present — and no Heretical Tome either. Ruling 7: the
  // slot holds ONE thing and it can never be replaced, which cuts both
  // ways and was only ever enforced in one direction.
  if (hasUhlti(being)) {
    return fail("R67", "Cannot train heretically if U'hlti is present");
  }
  if (hasHereticalTome(being)) {
    return fail("R67", "That slot already holds a Heretical Tome — Ruling 7, it cannot be replaced");
  }

  // Cost: 6 Khao AND 6 Anima — R67.
  //
  // ⚠ R96, THE TILL LAW, AND WHY `>=` WAS A BUG. This read
  // `availableAnima < anima`, which lets a seat holding EXACTLY 6
  // Anima pay 6 and land on 0. R33 says 0 Anima is a loss and R96 says
  // a VOLUNTARY payment is illegal rather than suicidal — the rite may
  // not kill the celebrant. canPayVoluntary is rules.js's single
  // implementation of that strict `>`; the board already used it and
  // this validator disagreed with the board.
  const { khao, anima } = HERETICAL_TRAINING_COST;
  if (availableKhao < khao) {
    return fail(
      "R67",
      `Heretical Training costs ${khao} Khao + ${anima} Anima; have ${availableKhao} Khao, ${availableAnima} Anima`
    );
  }
  if (!canPayVoluntary(availableAnima, anima)) {
    return fail(
      ["R67", "R96", "R33"],
      `Heretical Training costs ${anima} Anima and you hold ${availableAnima} — a voluntary payment may never take you to 0`
    );
  }

  return pass();
};

// ═══════════════════════════════════════════════════════════
// SUPREME BEING RULES
// ═══════════════════════════════════════════════════════════

/**
 * R69 — Supreme = Natural L3 + 3 Impera + U'hlti + Affinity
 * R70 — Affinity only attachable when Natural L3 has U'hlti
 * R71 — Escendant can only have 1 Impera of each level
 * R72 — Escendant can only have 1 U'hlti or Tome attached
 * R73 — Escendant can only have 1 Affinity attached
 */
export const validateSupremeBeing = (being, allCards = []) => {
  // Must be Natural L3
  if (!isNaturalBeing(being) || getBeingLevel(being) !== 3) {
    return fail("R69", "Supreme Being must be a Natural L3 Escendant");
  }

  // Must have exactly 3 Impera (L1, L2, L3)
  const soul = being.soul || [];
  const impera = soul.filter(id => {
    const card = getCard(id, allCards);
    return card && card.type === "Impera";
  });

  if (impera.length !== 3) {
    return fail("R69", `Supreme Being needs 3 Impera (1 of each level); has ${impera.length}`);
  }

  // Check levels 1, 2, 3 are represented
  const imperaLevels = impera.map(id => {
    const card = getCard(id, allCards);
    return card?.level;
  }).filter(Boolean).sort();

  if (JSON.stringify(imperaLevels) !== JSON.stringify([1, 2, 3])) {
    return fail("R69", "Supreme Being must have L1, L2, and L3 Impera");
  }

  // Must have U'hlti
  if (!hasUhlti(being)) {
    return fail("R69", "Supreme Being must have a U'hlti");
  }

  // Must have Affinity
  if (!hasAffinity(being)) {
    return fail("R69", "Supreme Being must have an Affinity");
  }

  return pass();
};

// ═══════════════════════════════════════════════════════════
// MISC RULES
// ═══════════════════════════════════════════════════════════

/**
 * R74 — Prophecies/Esclings unaffected by all cards
 */
export const validateTargeting = (target, targetType, opts = {}) => {
  if (targetType === "Prophecy" || targetType === "Escling") {
    return fail("R74", "Prophecies and Esclings cannot be targeted by card effects");
  }

  // ── R75 · TARGETING NEVER CROSSES BETWEEN CURIOS AND TOMES ──────
  // RULEZ_V2 ch.14, verbatim: *"A card that affects Tomes does not affect
  // Curios, and the reverse."*
  //
  // They are near-twins on purpose — a Curio activates, pays a fare,
  // carries OPT/OP2/OP3/ATV and can sit face-down, exactly as a Tome does
  // — and the ONE thing that separates them mechanically is which rift
  // they live in and therefore which cards can reach them. A Tome-ruining
  // effect that quietly took a Curio would be taking a card the adversary
  // committed to before the duel began and can never replace.
  //
  // `effectScope` is DECLARED by the call site, never guessed from printed
  // prose — traps 4.8 (printed text and branch disagreed on five cards)
  // and 4.4 (a regex-over-source check that could not fail) are both that
  // mistake. Absent, nothing is refused, so every existing call site keeps
  // its exact behaviour and only a caller that says which kind it means
  // gets the wall.
  const scope = opts.effectScope;
  if (scope === "Tome" && targetType === "Curio") {
    return fail("R75", "A card that affects Tomes does not affect Curios — they are different rifts (R75)");
  }
  if (scope === "Curio" && targetType === "Tome") {
    return fail("R75", "A card that affects Curios does not affect Tomes — they are different rifts (R75)");
  }

  return pass();
};

/**
 * R75 — Curios behave like Tomes with their own Rift
 */
export const validateCurioSlots = (curios) => {
  const count = (curios || []).filter(c => c).length;
  if (count > CURIO_SLOTS) {
    return fail("R75", `Max ${CURIO_SLOTS} Curio slots, have ${count}`);
  }
  return pass();
};

/**
 * R76 — Game-actions don't trigger Anima/Khao proc effects
 */
export const GAME_ACTION_SOURCES = ["draw", "refill", "reset", "upkeep", "turn-start", "turn-end", "graveshift", "setup"];

export const validateGameActionProcs = (source, procFired = false) => {
  // A GAME action is the engine moving the game along — the Turn draw,
  // the Khao refill, the Lyf reset. A PLAYER action is somebody
  // choosing something. Anima/Khao proc effects answer the second and
  // must never answer the first, or a deck with one on-gain trigger
  // loops forever on the upkeep.
  if (!procFired) return pass();
  const src = String(source || "").toLowerCase();
  if (GAME_ACTION_SOURCES.includes(src)) {
    return fail("R76", `A card proc fired on the game-action '${source}' — game actions do not trigger Anima/Khao procs`);
  }
  return pass();
};

// ═══════════════════════════════════════════════════════════
// REGRESSION CHECKS — Specific bugs that should never recur
// ═══════════════════════════════════════════════════════════

/**
 * B01 — Tome activated multiple times in one turn without OPT/OP2/OP3/ATV
 */
export const checkTomeMultiActivation = (tome, activationsThisTurn, allCards = []) => {
  if (!tome || activationsThisTurn === 0) return pass();

  const card = getCard(tome.id, allCards);
  if (!card) return pass();

  // Check for persistent tags
  const tags = card.tags || [];
  const hasPersistent = tags.some(t => PERSISTENT_TOME_TAGS.includes(t));

  if (activationsThisTurn > 0 && !hasPersistent) {
    return fail("B01", `Tome '${card.name}' activated ${activationsThisTurn} times without persistent tag`);
  }

  return pass();
};

/**
 * B02 — Non-persistent Tome remained on field after activation
 */
export const checkTomeRemainedOnField = (tome, justActivated, allCards = []) => {
  if (!justActivated || !tome) return pass();

  const card = getCard(tome.id, allCards);
  if (!card) return pass();

  const tags = card.tags || [];
  const hasPersistent = tags.some(t => PERSISTENT_TOME_TAGS.includes(t));

  if (!hasPersistent && tome.onField) {
    return fail("B02", `Non-persistent Tome '${card.name}' remained on field after activation`);
  }

  return pass();
};

/**
 * B03 — Anima hit BY A LYT ATTACK while a non-Fallen Escendant stood
 *
 * ⚠ THE `attackKind` PARAMETER IS THE 2026-08-26 RULING, AND ITS ABSENCE
 * WAS THE BUG THIS SIGNATURE HAD BEEN CARRYING QUIETLY. The old shape
 * took a bare `animaWasHit` boolean, which cannot tell a Lyt Attack from
 * a Tome — so the broad reading of R31 was baked into the API and left
 * open for whoever wired it up. caP'n: *"the wall of beings only stops
 * dimming of Anima from Lyt Attacks (excluding fallen states etc) —
 * precisely, it needs 'Lyt' in it."* A Tome / Impera / U'hlti / Curio /
 * Primera dim behind a standing wall is LEGAL and is not a B03.
 *
 * The default is "Lyt" because `isLytAttack(null)` is true and every
 * existing caller passed nothing — so nothing changes for the swing,
 * and a card dim now has a way to say what it is.
 */
export const checkAnimaBypassProtection = (field, animaWasHit, currentTurn = null, attackKind = "Lyt") => {
  if (!animaWasHit) return pass();
  // R31 — the wall binds the SWING. Anything else walks straight past it.
  if (!isLytAttack(attackKind)) return pass();

  // R26 (canon, July 2026): Fallen beings never protect Anima.
  // R31 (RULEZ_V2.md, canon; false 'July 2026' cite scrapped 2026-08-27): Fractured (0-Lyf) Escendants DO protect —
  // any non-Fallen Escendant on field means a LYT hit is a violation.
  const hasActive = (field?.esclings || []).some(s => {
    const raw = s?.__raw || s;
    if (!raw?.escendant) return false; // esclings don't protect Anima
    return !(s.isFallen || raw.fallen);
  });

  if (hasActive) {
    return fail("B03", "Anima was LYT-ATTACKED while an active Escendant was still on field");
  }
  return pass();
};

// ═══════════════════════════════════════════════════════════
// MASTER VALIDATION FUNCTION
// ═══════════════════════════════════════════════════════════

/**
 * Validate an entire action against all applicable rules.
 * Returns { valid, violations[], reason }
 */
// ═══════════════════════════════════════════════════════════
// THE EXILE WAVE — THE THREE ARTS (caP'n, 2026-09-06/07)
// ═══════════════════════════════════════════════════════════
// An Art is not a card. It is not drawn, not built into a Book, not
// attached to a being, and it cannot be refused for any of the reasons a
// card can. What it CAN be refused for is its own fare, its own cadence
// and its own gate, and those are the three things these validate.
//
// Every law they ask is DECLARED in rules.js and merely ASKED here. This
// file has no private copy of any of them — that property is the whole
// reason the catalogue exists, and it is what let R107's no-mixing law
// change one table and bind eleven call sites.

/**
 * R111 — an Exiliation. Four options, one taken; the fare is the LEVEL OF
 * THE CARD EXILED, not of the Art; once, then locked for two full Turns.
 */
export const validateExiliation = (
  option, exiledLevel, khao, anima, lastUsedTurn, currentTurn, seatCount = 2
) => {
  if (!EXILIATION_OPTION_IDS.includes(option)) {
    return fail("R111", `"${option}" is not one of the four Exiliations`);
  }
  const lvl = Number(exiledLevel) || 1;
  const fare = exileFare(lvl);
  // R107 — entirely one pool or entirely the other, and R96's Till Law
  // rides inside farePoolsFor: a payment may not take you to 0.
  const pools = farePoolsFor(fare, { khao, anima });
  if (!pools.length) {
    return fail("R111", `An Exiliation of a Level ${lvl} card costs ${fare} — payable in Khao or in Anima, never split, and never down to 0 Anima`);
  }
  // R111's cadence, measured through R103's helper like every other one.
  if (lastUsedTurn != null) {
    const ready = Number(lastUsedTurn) + exiliationCooldown(seatCount);
    if ((Number(currentTurn) || 0) < ready) {
      return fail("R111", `An Exiliation was performed on half-turn ${lastUsedTurn}; the next is available at ${ready} (once per 2 full Turns)`);
    }
  }
  return pass();
};

/**
 * R113 — the Ritual proper. TWO NUMBERS THAT ARE NOT THE SAME NUMBER: a
 * floor of 10 Anima to reach the rift at all, and then a fare of HALF what
 * you actually hold, rounded against you by R114.
 */
export const validateExileRetrieval = (
  cardId, exileRift, anima, lastUsedTurn, currentTurn, seatCount = 2, exileReturned = [], isEscendant = null
) => {
  const rift = Array.isArray(exileRift) ? exileRift : [];
  if (!rift.includes(cardId)) {
    // Covers the ownership clause too: a seat is only ever handed its own
    // rift, so "not in this rift" is also "not yours". caP'n: *"only ever
    // your cards."*
    return fail("R113", "That card is not in your Exile rift — and no Aenarchist may reach into another's, at any price");
  }
  if (isExileSpent(cardId, exileReturned, isEscendant)) {
    return fail("R113", "That card has already been bought back once. Once ever, per card");
  }
  if (!canReachExileRift(anima)) {
    return fail("R113", `The Ritual needs at least ${EXILE_RETRIEVAL_ANIMA_FLOOR} Anima to reach at all — you hold ${anima}`);
  }
  const fare = exileRetrievalFare(anima);
  // ⚠ THE TILL LAW STILL BINDS, and it is not decoration here. Half of an
  // ODD Anima total rounds UP (R114), so a seat on exactly 10 pays 5 and
  // lives, but the guard is asked rather than reasoned about — the one
  // exemption in the game is the Graveshift and this is not it.
  if (!canPayVoluntary(anima, fare)) {
    return fail("R113", `The Ritual would cost ${fare} of your ${anima} Anima and a voluntary payment may never take you to 0`);
  }
  if (lastUsedTurn != null) {
    const ready = Number(lastUsedTurn) + exileRetrievalCooldown(seatCount);
    if ((Number(currentTurn) || 0) < ready) {
      return fail("R113", `The Ritual was performed on half-turn ${lastUsedTurn}; the next is available at ${ready} (once per 3 full Turns)`);
    }
  }
  return pass();
};

/**
 * R115-R119, R121 — a Role Art. The rift owns the Art; the Escendant
 * standing there wears it. Asleep for three full Turns, silent in the Mind
 * rift while the trio holds, and bound only by its own cadence.
 */
/**
 * R119 — how many Warden Arts this seat has spent in the CURRENT FULL
 * Turn. Accepts the resolved number (legacy) or the raw `{turn, n}`
 * stamp, and resolves the raw one against the full Turn rather than the
 * half-turn — see the banner at the cap inside validateRoleArt.
 *
 * A full Turn is `halfTurnsPerFullTurn(seatCount)` half-turns, so the
 * two half-turns `6` and `7` of a duel are ONE Turn and share the cap.
 */
export const wardenArtsSpentThisFullTurn = (record, currentTurn, seatCount = 2) => {
  if (record == null) return 0;
  if (typeof record === "number") return Math.max(0, Math.floor(record) || 0);
  if (typeof record !== "object") return 0;
  const per  = Math.max(1, halfTurnsPerFullTurn(seatCount));
  const now  = Math.floor(Number(currentTurn) || 0);
  const then = Math.floor(Number(record.turn) || 0);
  if (Math.floor(now / per) !== Math.floor(then / per)) return 0;   // a different Turn
  return Math.max(0, Math.floor(Number(record.n) || 0));
};

export const validateRoleArt = (
  riftIndex, rifts, khao, anima, currentTurn, seatCount = 2,
  { level = 1, lastUsedTurn = null, wardenArtsThisTurn = 0 } = {}
) => {
  const line = Array.isArray(rifts) ? rifts : [];
  const i = Number(riftIndex);
  const role = roleOfRift(i);
  if (!role) return fail("R115", `Rift ${riftIndex} is not one of the four`);

  // R115 — the Art belongs to the rift, but it wakes for an ESCENDANT.
  // An Escling bears no Primera and therefore no Role.
  if (!riftArtIsAwake(line[i], currentTurn, seatCount)) {
    if (!rolesAwake(currentTurn, seatCount)) {
      return fail("R115", `The Arts of the rifts sleep for the first ${ROLE_LOCK_FULL_TURNS} full Turns — all four of them, the free ones included`);
    }
    return fail("R115", `The ${riftNameOf(i)} rift's Art wakes for an Escendant. An Escling bears no Primera, and no Role`);
  }

  // R121 — the Mind's Silence. Checked BEFORE the fare, so a silenced seat
  // is told why rather than being quietly unable to afford anything.
  // ── R121 · THE MIND'S SILENCE, AND WHY THERE IS NO REFUSAL HERE ──
  // A silenced Mind may perform NOTHING except the Warden Art of the rift
  // it is standing in — and R115 gives the Art to the RIFT, so a Role Art
  // performed FROM the Mind rift is the Warden Art by definition. There is
  // no other Art it could mean. The silence therefore never refuses this
  // action, and the refusal that used to sit here contradicted its own
  // failure message ("...may perform nothing but its own Warden Art").
  //
  // ⚠ AND IT IS A COMMENT RATHER THAN AN `if` ON PURPOSE. An earlier cut
  // kept the branch and added "ROLE_ART" to MIND_SILENCE_ALLOWS, which made
  // the condition unconditionally false — dead code that reads like
  // enforcement, which is worse than no code at all. The silence is
  // enforced where it bites: the Lyt swing (validateAttack), the stamp, the
  // Soul power and the Anima strike, all of which ask mindMayAct with their
  // OWN action type and are refused by the allow-list's default.
  if (i === MIND_RIFT && mindIsSilenced(line, currentTurn, seatCount)) {
    // Allowed, and deliberately so. No branch.
  }

  // ── R119 · ONE WARDEN ART PER AENARCHIST PER **FULL** TURN ──────
  // RULEZ_V2 ch.11, verbatim: *"**One Warden Art per Aenarchist per
  // Turn**, across both. Use the hand, and the Grimoire is shut until
  // your next Turn."*
  //
  // ⚠ "UNTIL YOUR NEXT TURN" IS A FULL TURN — two half-turns at a duel,
  // a full lap at a bigger table (R103). The counter this reads is
  // stamped `{ turn, n }` against `match.turn`, which counts HALF-turns,
  // and `roleArts.wardenArtsThisTurn` compares that stamp to the CURRENT
  // half-turn — so the cap emptied itself the moment the chair moved,
  // and a Warden could answer once on its own half-turn and again on its
  // adversary's. The Warden Art is an ANSWER; its natural moment IS the
  // adversary's half-turn, so that is not a corner case, it is the
  // common one.
  //
  // ⚠ AND THE WINDOW IS RESOLVED HERE, FROM THE RAW STAMP, so the unit
  // lives in the same file as the rule it belongs to. A caller may hand
  // this either shape:
  //   · a NUMBER — already resolved by the caller (the legacy shape that
  //     roleArts.js passes today). Taken at face value; the caller owns
  //     the window, and roleArts.js's own window is the one being fixed.
  //   · a RECORD `{ turn, n }` — the stamp exactly as it sits on the
  //     seat. Resolved against the FULL Turn, which is what the rule
  //     says, using halfTurnsPerFullTurn so a 3-4 seat table is right
  //     for free.
  // Reading the raw stamp is strictly better and is what ruleAdapter now
  // carries, so the day roleArts.js passes the record instead of the
  // number, the unit is already correct.
  if (role === ROLES.WARDEN) {
    const spent = wardenArtsSpentThisFullTurn(wardenArtsThisTurn, currentTurn, seatCount);
    if (spent >= WARDEN_ARTS_PER_TURN) {
      return fail("R119", `Each Aenarchist may use ${WARDEN_ARTS_PER_TURN} of their Warden Arts per full Turn, and this Turn's is spent`);
    }
  }

  // The cadence. ⚠ A ZERO HERE IS A REAL RULING, NOT AN ABSENCE: the Sol
  // rift has no cooldown by caP'n's explicit general ruling, so its pool
  // is its only brake. The contradiction gate in rules.js is what
  // guarantees a 0 means "no cooldown" rather than "no row".
  const cd = roleCooldown(role, seatCount);
  if (cd > 0 && lastUsedTurn != null) {
    const ready = Number(lastUsedTurn) + cd;
    if ((Number(currentTurn) || 0) < ready) {
      return fail("R115", `The ${riftNameOf(i)} rift's Art was used on half-turn ${lastUsedTurn}; the next is available at ${ready}`);
    }
  }

  const fare = roleFare(role, level);
  if (fare) {
    // R107 — and note the Body rift's square is 3 Khao OR 1 Anima, which
    // farePoolsFor handles natively because it asks each pool separately.
    const byKhao = (Number(khao) || 0) >= fare.khao;
    const byAnima = canPayVoluntary(anima, fare.anima);
    if (!byKhao && !byAnima) {
      return fail("R115", `The ${riftNameOf(i)} rift's Art costs ${fare.khao} Khao or ${fare.anima} Anima — entirely one or entirely the other, and never down to 0 Anima`);
    }
  }
  return pass();
};

export const validateAction = (action, gameState, allCards = []) => {
  if (!action || !gameState) {
    return fail("", "No action or game state provided");
  }

  // ══════════════════════════════════════════════════════════
  // ⚠ A MISSING SEAT IS A REFUSAL, NOT AN EXCEPTION (2026-09-14)
  // ══════════════════════════════════════════════════════════
  // FOUND IN A LIVE DUEL, not by any instrument. Twenty-odd cases below
  // read `gameState.me.khao`, `gameState.me.anima`, `gameState.me.field`
  // and friends with NO optional chaining, while the guard above only
  // asked whether `gameState` itself existed. That held for years because
  // every caller was a click handler, and by the time a human can click,
  // the Firebase snapshot has landed and `me` is an object.
  //
  // Then `roleArts.artsRailFor` began asking — from inside a React
  // `useMemo`, during RENDER, once per rift. On the first paint of a
  // Solrend `me` is still null, so `validateAction` THREW, `enforce`
  // caught it and logged
  //     [ruleGate] RULE GATE FAILED CLOSED · ROLE_ART · RulesEnforcer
  //     threw: Cannot read properties of null (reading 'khao')
  // eight times a frame, and the Body/Sol/Heart/Mind rail came up with
  // nothing legal on it. Failing closed meant nothing ILLEGAL happened —
  // the gate did its job — but the Arts were unreachable and the console
  // was full of a TypeError that read like a crash.
  //
  // ⭐ THE LESSON: the traps file says "a rule that only fires in the
  // renderer does not exist". This is its mirror — A BUG THAT ONLY FIRES
  // IN THE RENDERER IS INVISIBLE TO EVERY HEADLESS INSTRUMENT. Seventeen
  // suites, the balancer, the Debugger and ~1,900 assertions were green
  // while this threw on every paint, because none of them render and all
  // of them hand the gate a fully-built seat.
  //
  // A validator asked about a seat that is not on the board cannot answer
  // "legal", so it answers "no" — once, cleanly, instead of throwing from
  // whichever case happens to dereference first.
  if (!gameState.me) {
    return fail("", "The acting seat is not on the board yet — nothing is legal until it is.");
  }

  const violations = [];
  let reason = "";

  // ══════════════════════════════════════════════════════════
  // 🚦 R108 · THE PHASE GATE, ASKED FIRST OF EVERY ACTION
  // ══════════════════════════════════════════════════════════
  // caP'n, 2026-08-26: *"now you declare attack phase, you cannot play
  // any more cards."* It is asked HERE — ahead of the switch — for the
  // same reason ruleGate exists at all: a phase that only some action
  // types remembered to check would be a phase only some of the game
  // has. Every type routed below meets it, including any added later.
  //
  // ⚠ THREE THINGS THIS DELIBERATELY DOES NOT DO.
  //   1. IT DOES NOT ADVANCE THE PHASE. Declaring Attack is a seat's own
  //      explicit act; a validator that moved the pointer would make
  //      merely ASKING whether something is legal change the game.
  //   2. IT DOES NOT FAIL CLOSED ON A MISSING PHASE. `phaseOf` reads an
  //      absent phase as MAIN, so every match written before this rule —
  //      every save, replay and headless fixture — validates exactly as
  //      it did before. Only a seat that has DECLARED Attack is bound.
  //   3. IT NEVER LOOKS AT THE ADVERSARY'S PHASE. A response window on
  //      somebody else's Turn is not your Main phase and not your Attack
  //      phase; `gameState.me.phase` is the acting seat's, and a
  //      responder is not the acting seat.
  {
    const verdict = phaseAllows(action.type, phaseOf(gameState.me));
    if (!verdict.allowed) return fail("R108", verdict.reason);
  }

  // Route to appropriate validator based on action type
  switch (action.type) {
    case "TRANSCEND":
      return validateTranscend(
        action.escling,
        action.escendantL1,
        allCards
      );

    case "ASCEND":
      return validateAscend(
        action.fromLevel,
        action.toLevel,
        action.fromCard,
        action.toCard,
        action.isNatural,
        gameState.me.khao,
        gameState.me.anima,
        gameState.ascensionsThisTurn || 0
      );

    case "ATTACK":
      // R08 — no Lyt Attack on the opening Turn, checked before the
      // combat math. Previously unreachable: the only caller of
      // validateFirstTurnRestrictions was nobody.
      {
        const firstTurn = validateFirstTurnRestrictions(
          gameState.turn, action.isFirstPlayer || false, "attack",
          { attackType: action.attackType || "Lyt", seatCount: gameState.seatCount || 2 }
        );
        if (!firstTurn.valid) return firstTurn;
      }
      return validateAttack(
        action.attacker,
        action.defender,
        action.attackerSlotIndex,
        action.defenderSlotIndex,
        action.lytDamage,
        action.defenderAnima,
        gameState.defendersAttackedThisTurn || [],
        allCards,
        // R119/R121 — the two battle lines. `action.rifts` lets a caller
        // pass them explicitly; otherwise they are read off the game state,
        // where the renderer and the brain both already have them. Absent
        // on an old payload, both clauses stand down.
        action.rifts ?? gameState.opponent?.field?.esclings ?? null,
        action.attackerRifts ?? gameState.me?.field?.esclings ?? null,
        // R115 — and the clock, because both of those clauses sleep for
        // three full Turns. Without it they bound the duel from half-turn 0.
        gameState.turn ?? null,
        gameState.seatCount || 2
      );

    case "ATTACK_ANIMA":
      return validateAnimaAttack(
        gameState.opponent.field,
        action.targetAnima,
        gameState.opponent.anima,
        gameState.turn ?? null,
        action.hasDirectAnima || false,
        gameState.seatCount || 2
      );

    // ── R08 · THE BROAD ATTACK (2026-08-25) ─────────────────────
    // The one door for every Attack that is NOT a Lyt swing and NOT
    // the Anima strike: a Tome, an Impera, a U'hlti, a Curio, a
    // Primera stamp, an Affinity. caP'n's R08 ruling makes "Attack"
    // the broad word, and a broad word needs a door wide enough for
    // the things it covers. Outside the opening Cycle this passes —
    // it is R08's gate and nothing else's, so it can be put in front
    // of any damaging effect without that effect having to know which
    // other rules apply to it.
    case "ATTACK_EFFECT":
      return validateFirstTurnRestrictions(
        gameState.turn, action.isFirstPlayer || false, "attack",
        { attackType: action.attackType || "Effect", seatCount: gameState.seatCount || 2 }
      );

    // ── R08 + R88 · SEARCHING EITHER BOOK ───────────────────────
    // The fare itself is a KHAO_SPEND; this case is the opening-Cycle
    // suspension caP'n marked "*new*", and it is checked BEFORE the
    // fare so a seat is never told it cannot afford something it was
    // never allowed to buy.
    //
    // ⚠ `action.effectSearch` IS THE ONE THING THAT DECIDES WHO IS
    // SEARCHING (caP'n, 2026-08-26), and it is read straight off the
    // action rather than guessed from the source Book or the turn — a
    // Tome tutoring out of the Grimoire and a seat paying two Khao to
    // dig through it are the SAME payload and differ only in who
    // started it. That fact is knowable at the call site and nowhere
    // else, so the call site states it. Absent = the player's search.
    case "SEARCH":
      return multi(
        validateFirstTurnRestrictions(
          gameState.turn, action.isFirstPlayer || false, "search",
          { source: action.source || "GRIMOIRE", seatCount: gameState.seatCount || 2,
            effectSearch: action.effectSearch || false }
        ),
        // ── R106 · A LUXMOIRE SEARCH FINDS IMPERA. ONLY IMPERA. ──
        // caP'n, 2026-08-26: *"you can still only search the Lux for
        // Impera, not the U/A's."* U'hltis and Affinities live in the
        // Luxmoire since the lane ruling and may be DRAWN out of it —
        // but never tutored, *"because we added the U'hlti and Affinity
        // into the Lux we can't have it so easy."*
        //
        // Checked here rather than only in the picker because there are
        // three doors onto that pile (the player's modal, the NPC's
        // luxmoireSearchOne, and its extraWantIds Supreme-piece lane)
        // and two of them could reach an U'hlti. `found` is optional:
        // an opening-the-picker call has no card yet and passes.
        validateLuxSearchTarget(action.source, action.found, allCards)
      );

    // ── R90 · THE MULLIGAN ──────────────────────────────────────
    case "MULLIGAN":
      return validateMulligan(gameState.turn, {
        mulligansUsed: action.mulligansUsed || 0,
        drawsThisTurn: action.drawsThisTurn || 0,
        handSize: action.handSize != null ? action.handSize : (gameState.me?.hand || []).length,
        seatCount: gameState.seatCount || 2,
      });

    case "PLACE_TOME":
      return validateTomePlacement(gameState.me.field.tomes);

    case "ACTIVATE_TOME":
      return validateTomeActivation(
        action.tome,
        action.turnsOnField,
        allCards,
        // R122 — the cadence limb needs the half-turn clock. `turn` is
        // gameState's normalised zero-based half-turn count, which is the
        // same unit `tome.lastUsedTurn` is stamped in.
        { faceDown: action.faceDown, seatCount: gameState.seatCount || 2, turn: gameState.turn }
      );

    // ── R122 · THE CADENCE TAG ON A SOUL CARD ───────────────────
    // caP'n, 2026-09-13: *"every card, acronyms like OPT OP3 etc are
    // real, they are not just there to look cool."*
    //
    // ⚠ THIS CASE DID NOT EXIST. `gateSoulActivation` has been exported
    // from ruleGate.js with ZERO call sites since the phase gate was
    // built, and ACTIVATE_SOUL fell through this switch to the default,
    // so the only thing it ever enforced was R108's phase door. That is
    // a wrapper watching nothing — trap 4.2 — and the rule it was
    // missing is the one the cards have printed all along.
    case "ACTIVATE_SOUL":
      return validateSoulActivation(
        action.being,
        action.soulCard,
        gameState.turn,
        { seatCount: gameState.seatCount || 2, allCards }
      );

    case "GRAVESHIFT":
      return validateGraveshift(
        gameState.me.grimoire,
        gameState.me.necropolis,
        gameState.me.hand,
        gameState.me.anima,
        gameState.me.anima
      );

    case "RESURRECT":
      return validateResurrection(
        action.escling,
        action.fromLevel,
        action.toLevel,
        gameState.me.khao,
        gameState.me.anima,
        action.resurrectionsThisSolrend || 0, // per-instance tally COUNT (R44)
        gameState.me.necropolis,
        action.cardName,
        action.free || false, // free methods skip R47, never R44/R85
        action.passive || false,      // R86 — paid rite only
        action.graveshifted || false  // R86 — this copy has shifted
      );

    case "PRIMERA":
      // ── R08 IN FRONT OF THE STAMPS ────────────────────────────
      // The Phantom School stamp "dims the adversary's Anima directly
      // by 1, bypassing all beings" — that is an Attack in caP'n's
      // broad sense and it was reachable on the opening Cycle, since
      // R09 explicitly permits activation on Turn 1 and nothing told
      // the stamps apart. The Beast Ilk stamp ("if the user dims the
      // adversary's Anima this Turn, dim it by 1 more") is an
      // amplifier and is refused for the same reason rather than
      // being left to depend on a dim that R08 has already refused.
      // Every other stamp is untouched: Machine, Humanoid, Dragon
      // (both axes), Time and Space attack nobody and stay legal on
      // the opening Cycle exactly as R09 says they must.
      //
      // ⚠ THE SEARCH BAN AND THE ATTACK BAN ARE DIFFERENT BANS, AND A
      // STAMP CAN BE CAUGHT BY ONE AND NOT THE OTHER. This is the case
      // that proves it and it is why the note is here rather than in a
      // report somebody has to find:
      //   · MACHINE (Ilk) searches the Grimoire for a Beb, HUMANOID
      //     (Ilk) searches the Luxmoire for a Level 1 Impera. Both are
      //     a card's own printed power, so caP'n's 2026-08-26 ruling
      //     EXEMPTS them from the search ban — *"cards are exempt from
      //     the first turn search rule"* — and they are legal on the
      //     opening Cycle. Neither is asked the search question here at
      //     all, because a stamp is not the player's search action;
      //     they were never routed through the SEARCH case and must
      //     never be.
      //   · PHANTOM (School) and BEAST (Ilk) reach the adversary's
      //     Anima, so the ATTACK ban below still refuses them, and the
      //     search exemption has no bearing on that whatsoever.
      // Two bans, asked separately, and either one alone stops a press.
      {
        const attacks = (action.axis === "school" && action.word === "Phantom")
          || (action.axis === "ilk" && action.word === "Beast");
        if (attacks) {
          const r08 = validateFirstTurnRestrictions(
            gameState.turn, action.isFirstPlayer || false, "attack",
            { attackType: "Primera", seatCount: gameState.seatCount || 2 }
          );
          if (!r08.valid) return r08;
        }
      }
      return validatePrimera(
        action.word,
        action.axis,
        action.level,
        gameState.me.khao,
        gameState.me.anima,
        action.wordUsedThisTurn || false,
        action.beingUsedThisTurn || false,
        action.isOwnTurn !== false,
        action.inResponseWindow || false
      );

    case "ATTACH":
      return validateSoulAttachment(
        action.being,
        action.attachment,
        allCards
      );

    case "ATTACH_AFFINITY":
      return validateAffinityAttachment(action.being, allCards);

    // ── THE FIVE THAT WERE NEVER CALLED (wired 2026-08-25) ──────
    // validateFirstTurnRestrictions, validateHandLimit,
    // validateKhaoRefill, validateKhaoPayment and validateGameActionProcs
    // were defined, exported, and reachable from nothing. R07-R15 and
    // R76 were catalogue rows with an implementation and no caller.

    case "DRAW":
      return multi(
        validateFirstTurnRestrictions(
          gameState.turn,
          action.isFirstPlayer || false,
          "draw",
          {
            source: action.source || "GRIMOIRE",
            drawsThisTurn: action.drawsThisTurn || 0,
            seatCount: gameState.seatCount || 2,
          }
        ),
        // R76 — the Turn draw is a GAME action; nothing procs off it.
        validateGameActionProcs("draw", action.procFired || false)
      );

    case "DISCARD":
      // R10 — draw-then-discard: the hand AFTER the discard must be legal.
      return validateHandLimit(action.hand || gameState.me.hand);

    case "KHAO_SPEND":
      return multi(
        validateKhaoPayment(
          action.amount || 0,
          gameState.me.khao,
          action.mixable ? gameState.me.anima : null
        ),
        validateKhaoRefill(
          gameState.me.khao,
          gameState.me.maxKhao ?? STARTING_KHAO,
          action.spentThisTurn || false,
          gameState.turn,
          action.gained || false,
          gameState.seatCount || 2
        )
      );

    case "KHAO_REFILL":
      return validateKhaoRefill(
        gameState.me.khao,
        gameState.me.maxKhao ?? STARTING_KHAO,
        action.spentThisTurn || false,
        gameState.turn,
        action.gained !== false,
        gameState.seatCount || 2
      );

    // ── R32 · THE ANIMA CEILING (WIRED 2026-08-25) ──────────────
    // validateAnimaGain was written as "the one place that can be
    // asked, and the one place a mutation can redden", exported, and
    // then given ZERO CALL SITES — `grep -rn validateAnimaGain src
    // tools` returned its own definition and one comment pointing at
    // it. Meanwhile 28 bare `Math.min(MAX_ANIMA, …)` clamps sat at the
    // gain sites (23 in Shatterfield.js, 5 in NPC.js), which is
    // arithmetic every author has to remember rather than a rule that
    // cannot be forgotten. An unused validator is a worse lie than the
    // backwards guard it replaced, because it reads like coverage.
    //
    // ⚠ R32 CAPS, IT DOES NOT FORBID. This verdict is a PASS carrying
    // `value` (what to write), `clamped` (whether the ceiling bit) and
    // `spilled` (what would not fit). A seat on 48 drinking 5 lands on
    // 50 and keeps the 3 it could not hold — a caller that treats the
    // clamp as a refusal has misread the rule. The only FAIL here is a
    // negative "gain", which is a dim wearing the wrong name (R33).
    case "GAIN_ANIMA":
      return validateAnimaGain(
        action.currentAnima != null ? action.currentAnima : gameState.me.anima,
        action.amount || 0
      );

    case "LYT_ATTACK_FIRST_TURN":
      // R08, reachable on its own for callers that gate before ATTACK.
      return validateFirstTurnRestrictions(
        gameState.turn, action.isFirstPlayer || false, "attack",
        { attackType: action.attackType || "Lyt", seatCount: gameState.seatCount || 2 }
      );

    case "HERETICAL_TRAINING":
      return validateHereticalTraining(
        action.being,
        gameState.me.khao,
        gameState.me.anima,
        allCards
      );

    // ── THE EXILE WAVE ──────────────────────────────────────────
    case "EXILIATION":
      return validateExiliation(
        action.option, action.exiledLevel,
        gameState.me.khao, gameState.me.anima,
        action.lastUsedTurn ?? gameState.me?.exiliationUsedTurn ?? null,
        gameState.turn, gameState.seatCount || 2
      );

    case "EXILE_RETRIEVE":
      return validateExileRetrieval(
        action.cardId,
        action.exileRift ?? gameState.me?.exile,
        gameState.me.anima,
        action.lastUsedTurn ?? gameState.me?.exileRetrievalUsedTurn ?? null,
        gameState.turn, gameState.seatCount || 2,
        action.exileReturned ?? gameState.me?.exileReturned, action.isEscendant || null
      );

    case "ROLE_ART":
      return validateRoleArt(
        action.riftIndex,
        action.rifts ?? gameState.me?.field?.esclings,
        gameState.me.khao, gameState.me.anima,
        gameState.turn, gameState.seatCount || 2,
        {
          level: action.level || 1,
          lastUsedTurn: action.lastUsedTurn ?? null,
          wardenArtsThisTurn: action.wardenArtsThisTurn ?? gameState.me?.wardenArtsThisTurn ?? 0,
        }
      );

    // ⚠ AN UNKNOWN ACTION PASSES, AND THAT IS DELIBERATE — BUT IT IS ALSO
    // THE REASON THE THREE CASES ABOVE HAD TO BE WRITTEN THE DAY THE RULES
    // WERE. A missing case here is not a refusal and not an error: it is a
    // SILENT YES. So an Art whose validator was never wired would not fail
    // loudly in testing, it would simply be unenforced, and every guard
    // above it — the fare, the cadence, the Mind's Silence — would be
    // ceremony. The phase gate at the top of this function is the one
    // thing that fails CLOSED for an unlisted type, which is why the three
    // names also had to be added to ATTACK_PHASE_ALLOWS in rules.js.
    //
    // The permissive default itself stays: this validator is asked by
    // callers across five drivers and a great many legitimate actions have
    // no rule to check. Turning it into a refusal would break every one of
    // them to catch a mistake that a grep catches for free.
    default:
      return pass(); // Unknown actions pass through
  }
};

// ════════════════════════════════════════════════════════════════════
// 🔍 UNREACHED_VALIDATORS — WHICH OF THESE NOBODY ASKS, AND WHO ASKS
//    THE QUESTION INSTEAD
// ════════════════════════════════════════════════════════════════════
// Trap §4.7, in the words of the book it is written in: *"`validateAnimaGain`
// was added as 'R32's real seal', documented as the one place that can be
// asked and the one place a mutation can redden — and given ZERO call
// sites. An unused validator is a worse lie than the guard it replaced,
// because it reads as coverage."*
//
// Seventeen validators in this file have no caller and no live
// `validateAction` case. That is not automatically a fault: several of
// them are pre-duel or whole-board questions that a per-action validator
// is the wrong shape for, and something else genuinely asks them. What
// WAS a fault is that a reader could not tell those apart from the ones
// nobody asks at all — the `Validators` table below lists all of them
// side by side and says nothing about reach.
//
// So each one is named here with WHAT ASKS THE QUESTION INSTEAD, by
// FUNCTION NAME rather than by line number (every line-number citation
// in this repository's comments had rotted by 2026-09-14). A row that
// says "nothing" is a declared hole, not a tidied one.
//
// ⚠ THIS TABLE CANNOT LIE IN EITHER DIRECTION, and that is the whole
// point of writing it down. `tools/gateReach.selftest.mjs` fails if a
// validator here has GAINED a caller (delete the row and assert the
// refusal instead) and fails if a validator NOT here has lost its last
// one. A declared gap that the suite holds is structural; a remembered
// one is trap §4.4 wearing a table.
export const UNREACHED_VALIDATORS = {
  // ── PRE-DUEL SETUP — the board writes these, it never asks them ──
  validateStartingAnima:
    "R01 — gameManager.createMatch WRITES the opening 30 from rules.STARTING_ANIMA; there is no action to validate. The state is audited from the other side by GameStateValidator's S-codes.",
  validateStartingKhao:
    "R02 — gameManager.createMatch writes rules.STARTING_KHAO. Audited as a state by GameStateValidator (S09), not asked as an action.",
  validateStartingHand:
    "R03 — gameManager.createMatch deals the opening five through drawCards. Asked as a state by GameStateValidator's hand audit, never as an action anybody could take wrongly.",
  validateEsclingPlacement:
    "R04 — the four Esclings are placed pre-duel by starterDecks/DeckBuilder and audited by GameStateValidator (F01/F12). Nothing in a duel can add or remove one, so there is no action to gate.",

  // ── DECK LEGALITY — asked at SAVE, by hand, in DeckBuilder ──────
  validateGrimoireSize:
    "R05 — DeckBuilder's sanitize() refuses a short Grimoire at SAVE, comparing against the same rules.GRIMOIRE_MIN this reads. Also audited whole-board by GameStateValidator (G05).",
  validateLuxmoireSize:
    "R06 — DeckBuilder's sanitize() refuses both the floor and the ceiling at SAVE off rules.LUXMOIRE_MIN/LUXMOIRE_MAX. Audited by GameStateValidator (G05).",
  validateDeckLanes:
    "R05 — DeckBuilder's sanitize() MIGRATES a stray U'hlti or Affinity into the Luxmoire and strips pre-placed types rather than refusing, which is kinder and is what ships. Audited by GameStateValidator (G05).",
  validateDeckLimits:
    "R53-R63/R94 — the per-unique-name copy caps are enforced at the DeckBuilder stepper, which will not add past a card's own `deckLimit` (verified equal to rules.DECK_LIMITS on all 177 cards). ⚠ DECLARED HOLE: sanitize() does NOT re-check copies at save, so a deck that reached Firebase by any other route is never re-counted. One line in DeckBuilder closes it and this function now accepts the persisted {id: qty} shape so it can be that line.",
  validateAllyIlkCurios:
    "R62 — ⚠ NOTHING ASKS THIS, and GameStateValidator's own R62 row claims 'validateAllyIlkCurios reads it at deck time', which is a comment describing a call that does not exist. It is harmless TODAY only because R61 caps each Curio name at 1 and the shipped set holds exactly one Ally-Ilk Curio, so the cap of 2 cannot be reached. The day a second Ally Curio is minted this becomes live.",

  // ── WHOLE-BOARD SHAPES — the rift arrays make them unreachable ──
  validateCurioSlots:
    "R75 — the two Curio rifts are fixed by construction: every reader normalises through riftArray(field.curios, 2), so a third Curio has nowhere to be. Audited by GameStateValidator (F19).",
  validateSupremeBeing:
    "R69 — a Supreme Being is a LEGAL state rather than an illegal one, so there is no action to refuse. GameStateValidator's A11 watches the ways one can be built wrong, which is the answerable half.",
  validateDeviancyRestrictions:
    "R64 — asked, but by two other validators rather than this one: validateSoulAttachment's R70 clause refuses an U'hlti on a Deviant being and validateAffinityAttachment's isNaturalBeing check refuses the Affinity. Those are the only two things R64 forbids, and both are on the live ATTACH / ATTACH_AFFINITY paths.",

  // ── EVENTS, NOT STATES — and the two declared holes ─────────────
  validateGameEnd:
    "R33 — about eleven sites end a match the instant a seat's Anima reaches 0 (matchOutcome.finishOrEliminateSeat is the one that resolves N-seat elimination and co-winners). This is the shape of that question, kept because GameStateValidator's S02/S04 watch the same board condition and a mutation needs somewhere to land. It returns a real verdict now; before 2026-09-14 it returned {gameOver, loser, reason}, which validateAction could not have routed even if somebody had wired it.",
  validateTomeRuin:
    "R39/B02 — ⚠ NOTHING ASKS THIS. The ruin is a transition between two snapshots, and GameStateValidator's R39 row says outright that the rule 'is owned by nothing'. The Debugger's regression simulator owns B02. A live owner needs the resolvers to declare the ruin, which is a build.",
  checkTomeMultiActivation:
    "B01 — the Debugger's regression simulator owns it, over a whole simulated turn rather than one action. The LIVE half of the same rule is validateTomeActivation's R122 cadence limb, added 2026-09-14, which refuses the second firing at the moment it is attempted.",
  checkTomeRemainedOnField:
    "B02 — the Debugger's regression simulator owns it; it compares a Tome's persistence tag against whether it is still in its rift after a turn, which is a two-snapshot question this per-action file cannot ask.",
  validateTargeting:
    "R74/R75 — ⚠ NOTHING ASKS THIS YET. Its R74 limb (a Prophecy or an Escling can never be targeted) is enforced by per-card branches in both resolvers; its R75 limb (targeting never crosses between Curios and Tomes) was added 2026-09-14 and needs the resolvers' ruin/nullify funnels to declare `effectScope`. Until they do, the wall exists and nobody walks into it.",
};

// ═══════════════════════════════════════════════════════════
// EXPORT VALIDATORS FOR UNIT TESTING
// ═══════════════════════════════════════════════════════════

export const Validators = {
  // ── THE EXILE WAVE ──────────────────────────────────────────
  // Exported here as well as switched above, because a validator that
  // exists only inside a switch cannot be unit-tested and a validator
  // that is exported but never switched is worse than no guard at all —
  // it reads as coverage. R32's validateAnimaGain shipped that way once,
  // exported and documented as "the one place a mutation can redden",
  // with zero call sites. Both halves, or neither.
  Arts: {
    Exiliation: validateExiliation,
    ExileRetrieval: validateExileRetrieval,
    RoleArt: validateRoleArt,
  },
  Setup: {
    StartingAnima: validateStartingAnima,
    StartingKhao: validateStartingKhao,
    StartingHand: validateStartingHand,
    EsclingPlacement: validateEsclingPlacement,
    GrimoireSize: validateGrimoireSize,
    LuxmoireSize: validateLuxmoireSize,
  },
  // ── WIRED 2026-08-25 — these five were exported and absent here ──
  Turn: {
    FirstTurnRestrictions: validateFirstTurnRestrictions,
    HandLimit: validateHandLimit,
    // ── R90, WIRED 2026-08-25 — the mulligan was UI state, not a rule.
    Mulligan: validateMulligan,
  },
  Khao: {
    Refill: validateKhaoRefill,
    Payment: validateKhaoPayment,
  },
  Transcend: {
    Transcend: validateTranscend,
    Ascend: validateAscend,
  },
  Combat: {
    Attack: validateAttack,
    AnimaAttack: validateAnimaAttack,
    GameEnd: validateGameEnd,
  },
  // ── WIRED 2026-08-25 — R32's seal was exported and unreachable ──
  Anima: {
    Gain: validateAnimaGain,
  },
  Tomes: {
    Placement: validateTomePlacement,
    Activation: validateTomeActivation,
    Ruin: validateTomeRuin,
  },
  Graveshift: {
    Graveshift: validateGraveshift,
  },
  Primera: {
    Primera: validatePrimera,
  },
  Resurrection: {
    Resurrection: validateResurrection,
  },
  Soul: {
    Attachment: validateSoulAttachment,
    Affinity: validateAffinityAttachment,
  },
  Deviant: {
    Deviancy: validateDeviancyRestrictions,
    HereticalTraining: validateHereticalTraining,
  },
  Supreme: {
    SupremeBeing: validateSupremeBeing,
  },
  Misc: {
    Targeting: validateTargeting,
    CurioSlots: validateCurioSlots,
    GameActionProcs: validateGameActionProcs,
  },
  Deck: {
    Lanes: validateDeckLanes,
    Limits: validateDeckLimits,
    // R62 — the Ally-Ilk Curio cap, which used to be filed as
    // unenforceable because "there is no field to read". There is.
    AllyIlkCurios: validateAllyIlkCurios,
  },
  // ── THE 2026-08-26 CANON PASS ──
  Phase: {
    // R108's predicate itself lives in rules.js; this is the door into
    // it for a caller that wants to ask without building an action.
    Allows: (type, phase) => {
      const v = phaseAllows(type, phase);
      return v.allowed ? pass() : fail("R108", v.reason);
    },
  },
  Search: {
    LuxTarget: validateLuxSearchTarget,
  },
  Regression: {
    TomeMultiActivation: checkTomeMultiActivation,
    TomeRemainedOnField: checkTomeRemainedOnField,
    AnimaBypass: checkAnimaBypassProtection,
  },
};

export default {
  validateAction,
  Validators,
  pass,
  fail,
};
